import React, {state, useState, useEffect, useRef} from "react";
import PropTypes from 'prop-types';
import { ReactSketchCanvas } from "react-sketch-canvas";
import { IMaskInput } from 'react-imask';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
    Autobox,
    Autocomplete,
    Avatar,
	backdropClasses,
	Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
	FormControlLabel,
	FormGroup,
    IconButton,
    MenuItem,
    Paper,
	Radio,
	RadioGroup,
	Step,
	StepLabel,
	Stepper,
    Switch,
    TextField,
    Typography,
    FormLabel
} from "@mui/material";
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import InputAdornment from '@mui/material/InputAdornment';
import dayjs from "dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Grid from '@mui/material/Grid'
import LinearProgress, {linearProgressClasses} from '@mui/material/LinearProgress';
import {Controller, useFieldArray, useForm} from "react-hook-form"
import styles from './SubmissionForm.module.css'
import '@fontsource/montserrat'
import RDALogo from '../../img/RDALogo.png'
import StepContent from "@mui/material/StepContent";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FreeBreakfastOutlinedIcon from '@mui/icons-material/FreeBreakfastOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { useLocation, useNavigate } from 'react-router-dom';
import BLANK_PDF from "../../tempJSONFiles/blank.pdf"

//This function is set up to calulate and display the upper right corner with the proper percentage
//which is based of the current step/ number of total steps, but can be changed if desired
//This is currently done by keeping track of adding ten everytime the next step button is pressed
//and removing ten everytime the previous button is pressed
function LinearProgressWithLabel(props) {

    const logoBackground = {
        width:48,
        height:48,
        borderRadius: '50%',
        bgcolor:"#F8F5FE",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',


    }
    const progressBarStyle = {
        width: '318px', 
        height:"12px",
        borderRadius:"8px",
        backgroundColor: '#232226',
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: "8px",       
            backgroundColor: '#8B579E', 
        }
    }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent:'center', flexDirection:"column", fontFamily:'Monserrat', width:358, height:93 }}>
        <Box sx={{ display:'flex', minWidth: 318, marginBottom:"16px" }}>
        
            <Box sx={logoBackground}>
                <Box component="img" src={RDALogo} alt="RDA" sx={{width:"48px", height:"auto", objectFit:'contain'}} ></Box>
            </Box>
            
            <Box sx={{display:'flex', flexDirection:'column', marginLeft:"12px"}}>
                <Typography>
                    RDA Registration Form
                </Typography>     
                <Typography variant="body2" sx={{ color: '#CDCCD0' }}>
                    {`${Math.round(props.value)}% completed`}
                </Typography>
            </Box>
        </Box>
      <Box sx={{ width: '318px', height:"12px", mr: 1,}}>
        <LinearProgress sx={progressBarStyle} variant="determinate" {...props} />
      </Box>
      
    </Box>
  );
}

//This sets the type of what can be used as to determine the progress bar to be a number
//Could probably be removed since we already control what is sent to the progress section
//but as it currently works I choose to leave it in until the page gets more refined
LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
};

//This is general textfield that is used throughtout the form for user input. It currently is set up to allow
//for custom placeholder texts, labels, and error messages. Also can be used to allow more custom textfield, like combolists
//but in the future those should be changed to their own components. Design of the form is from some css page designs, and
//an sx varibale that is passed in. Should be changed to have the design set up based on the theme but that will be done
//in the future.
const LabeledInput = ({ label, name, register, rules, sx, children, error, ...rest }) => {
  
    const registerProps = register ? register(name, rules) : {};

    return (
    <div style={{paddingTop: "12px", paddingBottom: "12px"}}>
        <div className={styles.labelText}>
            {label}
        </div>
        <TextField
            sx={sx} 
            {...registerProps} 
            error={!!error} 
            helperText={error ? error.message : null} 
            {...rest} 
        >
            {children}
        </TextField>
    </div>
  );
};

//This is a similar component to the LabeledInput textfield component but is used when a calendar selector is needed
//It takes in 2 sx varibals to determine the style of the textfield and the style of the calendar popup. This should
//be changed to be set up with the theme for the future.
const LabeledCalendarInput = ({ label, name, control, rules, sx, sx2, children, ...rest }) => {
  return (
    <div style={{paddingTop: "12px", paddingBottom: "12px"}}>
        <div className={styles.labelText}>
            {label}
        </div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <DatePicker
                        value={value || null}
                        views={['year', 'month', 'day']} 
                        onChange={onChange}
                        disableFuture
                        sx={sx}
                        slotProps={{
                            textField: {
                                error: !!error,
                                //This disables the ability to type in the date and forces the picker to be used. This is done so that way the age field will always be valid
                                //If this is desired to be changed considerations will need to be made.
                                //onKeyDown: (e) => {e.preventDefault();},     
                            },
                            desktopPaper: {
                                sx: sx2
                            },
                        }}
                        //This changes the displayed day to be the three letter format I.E. M->Mon
                        dayOfWeekFormatter={(date) => date.format('ddd')}
                        //This has the calendar show the days outside of the current month 
                        showDaysOutsideCurrentMonth
                        {...rest}
                    />
                )}
            />
        </LocalizationProvider>
    </div>
  );
};

//This is the textfield specifically for phone numbers. It uses a mask to determine what can be inputted into the textfield.
//To match the style desired a startAdornment adds a phone icon and the "+1" so that only the next ten digits are tracked.
//It also takes a sx to determine the style which should be fixed with themes in the future.
const LabeledPhoneInput = ({ label, name, control, rules, sx, phoneMask, ...rest }) => {
  return (
    <div style={{paddingTop: "12px", paddingBottom: "12px"}}>
        <div className={styles.labelText}>
            {label}
        </div>
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue="" 
            render={({ field, fieldState:{error} }) => (
                <TextField
                    {...field}
                    {...rest} 
                    sx={sx}
                    error={!!error}
                    helperText={error ? error.message : null} 
                    slotProps={{ 
                        input:{
                            inputComponent: phoneMask,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LocalPhoneOutlinedIcon sx={{ color: "#686673" }} />
                                    <span style={{ 
                                        marginLeft: "8px", 
                                        marginRight: "4px",
                                        color: "#F8F5FE", 
                                        fontSize: "16px", 
                                        fontWeight: "400"
                                    }}>
                                        +1
                                    </span>
                                </InputAdornment>
                            ),
                        } 
                    }}
                    
                />
                )}
        />
    </div>
  );
};

//This component is used as the header for each step. The style is being manually set in the component so this
//should be changed in the future to be determined by the theme
const SectionDescription =({title, description}) => {
    return(
        <Grid item xs={12}>
            <Typography sx={{fontSize: 24 }}>
                {title}
            </Typography>
            <Typography sx={{color:'#CDCCD0', spacing: "8px"}}>
                {description}
            </Typography>
        </Grid>
    )
}

//This is the set up for any basic dropdown list component in the format by using the MUI Autocomplete component
//It has the current value be one from the provided list, determined by the options variable, or null, then
//the chosen option is placed in the textfield. The style is determined by two sx variables that should be
//changed to be based of the theme in the future. 
const BasicListField = ({ options, textfieldSx, dropDownSx, control, label, name, rules, ...rest }) => {
    return (
        <div style={{ paddingTop: "12px", paddingBottom: "12px" }}>
            <div className={styles.labelText}>
                {label}
            </div>

            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                    <Autocomplete
                        options={options}
                        value={options.find((opt) => opt.value === value) || null}
                        onChange={(event, newValue) => {
                            onChange(newValue ? newValue.value : "");
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === "string") return option; 
                            return option?.label || "";
                        }}
                        isOptionEqualToValue={(option, value) => option.value === value}
                        slotProps={{
                            paper: {
                                sx: dropDownSx
                            }
                        }}

                        renderInput={(params) => (
                            <TextField
                                {...params}
                                sx={textfieldSx}
                                inputRef={ref}
                                error={!!error}
                                {...rest}
                            />
                        )}
                    />
                )}
            />
        </div>
    );
}


//This is the component that takes a list of information for merchandise and will display it
//properly onto the screen, allowing for it to be flexible for every event.
//It takes an array of merchandise information and an array of sizes
//and uses them both to fil out the parts of the components 
const MerchInput =({merchName, namePrefix, sx, dropDownSx, textfieldSx, sizes, register, control, field}) => {
    return(
        <div>
            <div className={styles.labelText} style={{fontSize:"20px"}}>{merchName.label}</div>
            <Box  sx={{display:"flex", gap: 2, flexWrap: "wrap", justifyContent:"flex-start"}}>
                <BasicListField label="Size" placeholder="Select Size" control={control} name={`${namePrefix}${merchName.id}Size`} rules={{ required: false }} textfieldSx={textfieldSx} dropDownSx={dropDownSx} options={sizes}>
                </BasicListField>
                <LabeledInput  {...field} type="number" label="Quantity" placeholder="0" defaultValue="0"  rules={{ required: false }} 
                    sx={{...textfieldSx,"& .MuiOutlinedInput-input": {
                        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                            display: "none",
                            margin: 0,
                        },
                        "MozAppearance": "textfield",
                    },}}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex', flexDirection: 'column', mr: -1 }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => field.onChange(Number(field.value || 0) + 1)}
                                        sx={{ p: 0, color: "#CCB2D5" }} 
                                    >
                                        <ArrowDropUpOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => field.onChange(Math.max(0, Number(field.value || 0) - 1))}
                                        sx={{ p: 0, color: "#CCB2D5" }}
                                    >
                                        <ArrowDropDownOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </InputAdornment>
                        ),}}>
                </LabeledInput>


            </Box>
        </div>
    )
}

//This component is used to create the Authroized Transport list that can be expanded to the amount 
//needed per dancer. It currently uses sx to determine the style which should be changed in the future.
const AuthorizedTransport = ({sx, index, register, control, errors, onRemove, showRemove}) => {
    
    return(
        <Box sx={{display:"flex", flexDirection:"column"}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ fontWeight: 'bold', mb: 1 }}>Authorized Person {index + 1}</Box>
                {showRemove && (
                    <IconButton onClick={() => onRemove(index)} sx={{ color: '#FF5555' }}>
                        <DeleteOutlineIcon />
                    </IconButton>
                )}
            </Box>

            <Box sx={{display:"flex",gap:2, flexWrap: "wrap", justifyContent:"flex-start"}}>
                <Grid item >
                    <LabeledInput label="Full Name" placeholder="Enter the authorized person name" register={register} name={`authorizedPeople.${index}.name`} error={errors.authorizedPeople?.[index]?.name} rules={{ required: "Enter full name" }} sx={sx}/>
                    <LabeledInput label="Relationship" placeholder="Enter the relationship" register={register} name={`authorizedPeople.${index}.relationship`} error={errors.authorizedPeople?.[index]?.relationship} rules={{ required: "Enter relationship" }} sx={sx}/>
                </Grid>
                <Grid item>
                    {/* <LabeledInput label="Phone Number" placeholder="Enter phone number" register={register} name={`authorizedPhone${index}`} rules={{ required: true }} sx={sx}/> */}
                    <LabeledPhoneInput label="Phone Number" control={control} name={`authorizedPeople.${index}.phone`} sx={sx}  phoneMask={phoneMask} error={errors.authorizedPeople?.[index]?.phone}
                                        rules={{required:"Phone number is required", validate:(value) =>{
                                            if (!value || value.length !== 10) {
                                                return "Please enter all required digits";
                                            }
                                            return true;
                                         }
                                        }}/>
                    <LabeledInput label="Email" placeholder="name@example.com" register={register} name={`authorizedPeople.${index}.email`} rules={{ required: "Invalid email format. Use: name@email.com", pattern:{value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Please enter a valid email address" } }} error={errors.authorizedPeople?.[index]?.email} sx={sx} slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <MailOutlinedIcon 
                                                            sx={{ color:"#686673" }} 
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }
                                        }}
                                        />
                    {/* <LabeledInput label="Email" placeholder="Enter email" register={register} name={`authorizedEmail${index}`} rules={{ required: true }} sx={sx}/> */}
                </Grid>
            </Box>
        </Box>
    )
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

//This is using a mask to set up the input for the phone number so
//it will be in the proper style only allowing numbers as well
const phoneMask = React.forwardRef(function PhoneMask(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                // 1. Define the specific mask pattern
                mask="(000) 000-0000" 
                definitions={{
                    '0': /[0-9]/, // '0' accepts digits 0-9
                }}
                inputRef={ref}
                
                // 2. CRITICAL: Show the underscores immediately
                lazy={false}
                placeholderChar="_" 
                
                // 3. Handle the change event so it talks to MUI/React Hook Form
                onAccept={(value,mask) => onChange({ target: { name: props.name, value:mask.unmaskedValue } })}
                overwrite // Ensures typing replaces the underscore rather than pushing it
            />
        );
});

//This function is used to create the list of all events a dancer can sign up for. It works by
//taking an event object, which is the varibale event, and using that to map to the needed fields.
//It currently also contains just two options for selection for the radio buttons, however this should
//be changed to take a list from the event object that contains all the desired options to be returned.
//Also some of the features like the logo and sx are hard coded in and should be made to match the them
//or be supplied by the event object as well.
function EventCard({event, control, fieldName}) {

    const logoBackground = {
        width:48,
        height:48,
        borderRadius: '50%',
        bgcolor:"#F8F5FE",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',


    }

  return (
    <Box sx={{ display: 'flex', justifyContent:'center', flexDirection:"column", fontFamily:'Monserrat', backgroundColor:"#1C1A25", paddingLeft:"16px", paddingTop:"8px", borderRadius:"16px", width:"476px"}}>
        <Box sx={{ display:'flex', minWidth: 318, marginBottom:"16px" }}>
        
            <Box sx={logoBackground}>
                <Box component="img" src={RDALogo} alt="RDA" sx={{width:"48px", height:"auto", objectFit:'contain'}} ></Box>
            </Box>
            
            <Box sx={{display:'flex', flexDirection:'column', marginLeft:"12px"}}>
                <Typography>
                    {event.title}
                </Typography>     
                <Typography variant="body2" sx={{ color: '#CDCCD0' }}>
                    {event.location}
                </Typography>
            </Box>
        </Box>
        <Box sx={{ width: '476px' }}>
            <Controller
                name={fieldName}
                control={control}
                defaultValue="" 
                // No rules prop here means no validation so depending on the desired functionality this may need to be changed.
                render={({ field }) => (
                    <RadioGroup {...field} sx={{ justifyContent: "space-between" }}>
                        {event.options && event.options.map((option) =>(
                        <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontSize: "14px", color: "#F8F5FE" }}>
                                                {option.label}
                                            </Typography>
                                            {option.subtext && (
                                                <Typography sx={{ fontSize: "12px", color: "#686673" }}>
                                                    {option.subtext}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                    control={
                                        <Radio
                                            onClick={() => {
                                                if (field.value === option.value) {
                                                    field.onChange("");
                                                }
                                            }}
                                            sx={{
                                                color: "#686673",
                                                '&.Mui-checked': { color: "#CCB2D5" }
                                            }}
                                        />
                                    }
                                />
                            ))}
                        
                    </RadioGroup>
                )}
            />
            <Box >
                <Typography align="right" sx={{paddingRight:"16px",paddingBottom:"8px"}}>
                    {event.date}
                </Typography>   
            </Box>
        </Box>
      
    </Box>
  );
}

const SubmisssionForm = () => {

    

    //This is the react-hook-form const which should be holding the variables
    //as well as the methods for the form. It also allows for errors to be tracked
    //This method was choosen because it reducing the amount of state variables that would manually need 
    //to be set up.
    const {register, unregister, handleSubmit, watch,setValue, getValues, trigger, formState: { errors }, control} = useForm( {shouldUnregister: false, mode:"onSubmit",reValidateMode: "onChange", 
        defaultValues:{
            uniqueID:"",
            fileUploads:[],
            authorizedPeople: [{ name: "", relationship: "", phone: "", email: "" }],
            emergency:[]
            
        }
    })

        const location = useLocation();
        const navigate = useNavigate();
        // const accessCode = location.state?.accessCode;
        // console.log("Full Location State:", location.state);
        // useEffect(() => {
        //     if (accessCode) {
        //         setValue("uniqueID", accessCode);
        //         console.log("Wizard initialized with ID:", accessCode);
        //     } else {
        //         console.warn("No access code found. Redirecting to accessCode page.");
        //         navigate('/accessCode');
        //     }
        // }, [accessCode, setValue, navigate]);

        const accessCode = location.state?.accessCode || sessionStorage.getItem('currentAccessCode');

        useEffect(() => {
            if (accessCode) {
                setValue("uniqueID", accessCode);
                //console.log(accessCode)
            } else {
                navigate('/accessCode');
            }
        }, [accessCode, setValue, navigate]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: "authorizedPeople", 
    });

    //This is the submit method for when the submit button is pressed. Here is where the format and data
    //in the form would need to be checked and managed before it would be sent to the database.
    const onSubmit = async(data) => {
        // console.log("All Data:", JSON.parse(JSON.stringify(data)));
        // console.table(data)

        let isValid = false;
    
        if(calculatedAge<18){
            isValid = await trigger(["parentSignName", "parentStreetAddress", "parentCityStateZipcode", "GuardianWaiverSignature", "participantWaiverSignature", "participantStreetAddress", "participantCityStateZipcode", "printedSignName"]);
        } else{
            isValid = await trigger([ "participantWaiverSignature", "participantStreetAddress", "participantCityStateZipcode", "printedSignName"]);

        }
        if(isValid){
            const signedWaivers = await handleSubmitAgreement();

            
            const formData = new FormData();

            
            signedWaivers.forEach((file) => {
                formData.append("waivers", file);
            });
            const replacer = (key, value) => (value === undefined ? null : value);
            formData.append("mainData", JSON.stringify(data, replacer));
            data.fileUploads.forEach((file) => {
                formData.append("medicalCards", file); 
            });
            // console.table(data)
            try {
                const response = await fetch("/api/submit-form", {
                    method: "POST",
                    body: formData
                });
                const result = await response.json();

                if (result.success) {
                    console.log("success");
                    navigate('/successPage'); 
                    setTimeout(() => {
                        sessionStorage.removeItem('currentAccessCode');
                    }, 100);
                    
                } else {
                    alert(result.message);
                }
            } catch (error) {
                console.error("Submission failed", error);
            }
            
        }
    }

    const onError = (errors) => {
    console.error("Form Validation Failed:", errors);
};

    //The watch statements make use of the watch function of react-hook-form to keep
    //track of the variables needed for other calulations. Do note that use of
    //the watch function tracks every change of state so it should be used when
    //restricting the amount of changes.

    //This one is used to for the radio switch determining if the dancer is/was an RDA Member
    const RDAMember = watch("RDAMember")

    //This one determines if the rest of the housing page is needed by tracking if the dancer is interested
    const housingCheck = watch('housingCheck')

    //This one watches date of birth so age can be determined
    const birthdayValue = watch('dateOfBirth')

    //This one is for keeping track of the files uploaded for the insurance card
    const currentFiles = watch("fileUploads");

    //These are for keeping track of which events the user has signed up for
    const DCIRosewell26watch = watch("DCIRosewell26")
    const DCILawrence26watch = watch("DCILawrence26")
    const DCICarlisle26watch = watch("DCICarlisle26")
    const PWKingwood26watch = watch("PWKingwood26")
    
    //This is for keeping track of the dancer's name
    const dancerName = watch("fullName")
																			
																			


    //This is the calulation for the getting the how many years old the Dancer is
    //as of time filling out the form
    const calculatedAge = (birthdayValue && dayjs(birthdayValue).isValid()) ? dayjs().diff(birthdayValue, 'year') : "";

    //These next two functions are used to add the picture files of the insurance cards
    //to the form and be able to remove them from the list. It uses the setValue function
    //from the react-hook-form
    //This one adds the files to the array
    const handleFileChange = (e) => {
        // const newFiles = Array.from(e.target.files); 
        // const updatedFiles = [...(currentFiles || []), ...newFiles];
        // setValue("fileUploads", updatedFiles, { shouldValidate: true });

        const newFiles = Array.from(e.target.files); 
        
        const existingFiles = getValues("fileUploads") || [];

        const updatedList = [...existingFiles, ...newFiles];

        setValue("fileUploads", updatedList, { shouldValidate: true });

        e.target.value = '';

    };
    //This removes the selected file from the array
    const handleRemoveFile = (indexToRemove) => {
        // const updatedFiles = currentFiles.filter((_, index) => index !== indexToRemove);
        // setValue("fileUploads", updatedFiles, { shouldValidate: true });

        const existingFiles = getValues("fileUploads") || [];
        const updatedList = existingFiles.filter((_, i) => i !== indexToRemove);
    
        setValue("fileUploads", updatedList, { shouldValidate: true });

    };


    //This is used to track the percantage done
    const [progress, setProgress] = React.useState(0);

    //This is the style for the exit Button should eventually be done by themeing
    const exitButtonStyle = {
        borderRadius:999,
        backgroundColor:"#8B579E",
        textTransform:"none",
        fontSize:14,
        width:318,
        height:52,
    }
    
    //This is the style for the navigation buttons should eventually be done by themeing
    const navButtonStyle = {
        borderRadius:999,
        backgroundColor:"#302C3F",
        textTransform:"none",
        fontSize:14,
        width:200,
        height:52,
    }

    //This section is for the Stepper in the Wizard

    //This sets what the current step the user is on
    const [activeStep, setActiveStep] = React.useState(0);

    //This handles the next button process, using the react-hook-form to check if the current step
    //has been filled out before moving on to the next
    const handleNext = async () => {
        let isValid = false;
        // console.log(watch("dancerPhoneNumber"))

        if (activeStep === 0){
            isValid = await trigger(["fullName", "dateOfBirth", "age", "dancerEmail", "dancerPhoneNumber"]);
        }else if (activeStep === 1){
            if(calculatedAge<18){
                isValid = await trigger(["guardianFullName", "guardianEmail","confirmGuardianEmail", "guardianPhoneNumber", "emergencyContactName", "emergencyContactEmail","emergencyContactRelationship", "emergencyContactPhoneNumber",]);
            } else{
                isValid = await trigger(["emergencyContactName", "emergencyContactEmail","emergencyContactRelationship", "emergencyContactPhoneNumber"]);
            }
        }else if (activeStep === 2){
            const isSelectionValid = await trigger("RDAMember");
            if (!isSelectionValid) return;
            if(RDAMember==="Yes"){
                isValid = await trigger(["RDARegion", "currentRDADanceSchoolCompany"])
            }else{
                isValid = await trigger(["currentDanceSchoolCompany"])
            }
        }else if (activeStep === 3){
            const currentValues = getValues();
            const hasSelection = events.some(event => {
                const val = currentValues[event.id];
                return val !== "" && val !== undefined && val !== null;
            });
            if (!hasSelection) {
                setSelectionError(true);
                return; 
            }
            isValid=true;
        }else if (activeStep === 4){
            if(calculatedAge<18){
                isValid = await trigger(["allergies", "prescribedMedications", "prescribedNarcotics", "OTCMedication", "medicalConditions", "physicianName", "physicianPhoneNumber", "healthInsuranceProvider", "policyNumber", "fileUploads", "dancerHealthSignature", "guardianFullNameHealthSign", "guardianHealthSignature" ]);
            }else{
                isValid = await trigger(["allergies", "prescribedMedications", "prescribedNarcotics", "OTCMedication", "medicalConditions", "physicianName", "physicianPhoneNumber", "healthInsuranceProvider", "policyNumber", "fileUploads", "dancerHealthSignature",]);

            }
        }else if (activeStep === 5){
            if(watch("housingCheck") === "Yes"){
                isValid = await trigger(["dietaryRestrictions"]);
            }else{
                isValid=true;
            }
        }else if (activeStep === 6){
            isValid = true;
        }else if (activeStep === 7){
            //isValid = await trigger("authorizedPeople");
            isValid =true;
        }else if (activeStep === 8){
            isValid = true;
        }else if (activeStep === 9){
            isValid = true;
        }
        if (isValid){
            setSelectionError(false);
            setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, 9));
            setProgress((prevProgress) => Math.min(prevProgress + 10, 100));
        }
        
    };

    //This handles the back button process
    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
        setProgress((prevProgress) => Math.max(prevProgress - 10, 0));
    };

    //This is a process for a reset button that was used in development and has just been 
    //left in, since it may have a use but will likely just be removed
    // const handleReset = () => {
    //     setActiveStep(0);
    // };
    
    //This is the list of steps that are used and their information allowing for the amount 
    // of steps to be changed if needed in the future
    const steps =[
        {
            label: "Step 1",
            description:"Personal Info",
            icon:<PersonOutlineOutlinedIcon />
        },
        {
            label: "Step 2",
            description:"Guardian Info",
            icon:<SupervisorAccountOutlinedIcon />
        },
        {
            label: "Step 3",
            description:"RDA Affliation",
            icon:<GppGoodOutlinedIcon />
        },
        {
            label: "Step 4",
            description:"Programs",
            icon:<EventNoteOutlinedIcon />
        },
        {
            label: "Step 5",
            description:"Health & Safety",
            icon:<FavoriteBorderOutlinedIcon />
        },
        {
            label: "Step 6",
            description:"Housing & Meals",
            icon:<FreeBreakfastOutlinedIcon />
        },
        {
            label: "Step 7",
            description:"Merchandise",
            icon:<ShoppingBasketOutlinedIcon />
        },
        {
            label: "Step 8",
            description:"Transportation",
            icon:<DirectionsBusOutlinedIcon />
        },
        {
            label: "Step 9",
            description:"DCI Observation & Tickets",
            icon:<LocalActivityOutlinedIcon />
        },
        {
            label: "Step 10",
            description:"Acknowledgment & Signature",
            icon:<HistoryEduOutlinedIcon />
        },
    ]

    //These next are styles that will need to be changed with themeing in the future
    const stepsStyle= {
                  width: 318,
                  height:68,
                  padding: '12px',
                  borderRadius: "20px",
                  marginBottom:"12px", 
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  '& .MuiStepLabel-labelContainer': {
                    width: '100%',
                  },
                  '& .MuiStepLabel-label': {
                     color: 'white !important', 
                  }
    }
    const progressStyle ={
        display:'flex', 
        justifyContent:'center', 
        width: 358, paddingTop:'36px', 
        borderStyle:"solid", 
        borderColor:"#232226",
        borderWidth:"0 1px 1px 0",
        height: 'calc(100vh - 193px)',
        overflow: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }, 
    }
    const midRightStyle ={
        display:'flex', 
        maxWidth:"1012px",
        width:"100%",
        justifyContent:'center', 
        paddingTop:'36px', 
        height: 'calc(100vh - 193px)',
        overflow: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }, 
    }
    //This is used to scroll the stepper box automatically so the user doesnt have to do it
    useEffect(() => { 
        const element = document.getElementById(`step-${activeStep}`);

        if (element) {
            element.scrollIntoView({
            behavior: 'smooth', 
            block: 'nearest',             
            });
    }
        }, [activeStep]
    ); 

    const textboxStyle={
		width: "476px",
        borderRadius: "16px",
		//This is for styling the base textfield
		"& .MuiOutlinedInput-root": {
			backgroundColor: "#1C1A25",
			color: "#F8F5FE",
			borderRadius:"16px",
            height: "52px",
            overflow:"hidden",

		//This is for styling the default border
			'& fieldset': {
				borderColor: '#302C3F',
				borderWidth: '1px', 
			},
		// //This is for styling fieldset on hover
		 	'&:hover fieldset': {
		 		borderColor: '#302C3F',
				borderWidth: '1px'
		 	},
		// This is for styling the fieldset when clicked
		 '&.Mui-focused fieldset': {
		 	borderColor: '#302C3F',
			borderWidth: '1px'
		 	},

        // This is for disabled fieldsets
            "&.Mui-disabled": {
                backgroundColor: "#13111B !important", 
                color: "#F8F5FE !important",           
                opacity: "1 !important",               
                
                
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#302C3F !important", 
                    borderStyle: "solid !important",   
                },
                
                
                "& .MuiOutlinedInput-input": {
                    color: "#fef5f5ff !important",       
                    "WebkitTextFillColor": "#F8F5FE !important", 
                    height:"100%"
                },

                
                "& .MuiSvgIcon-root": {
                    color: "#686673 !important",       
                },
                "& .MuiOutlinedInput-input:not(:placeholder-shown)": {
                    backgroundColor: "#1C1A25 !important",
                    overflow:"hidden" 
                },
            },
		 },
		// This is for styling the input text
		"& .MuiInputBase-input": {
			color: "#F8F5FE", 
		},
        // This is for styling the Label
		'& .MuiInputLabel-root': {
			color: '#F8F5FE',
			'&.Mui-focused': {
                color: '#F8F5FE',
            },
		},
        //This is for the arrow in the dropdown options
        '& .MuiSelect-icon': {
            color: '#F8F5FE', 
        },
        '& .MuiInputLabel-root': {
            color: '#F8F5FE',
        },
        "& .MuiAutocomplete-popupIndicator": {
        color: "#F8F5FE", 
        transition: "color 0.2s", 
        
        // Change color when you hover over the arrow itself
            "&:hover": {
                backgroundColor: "transparent", 
                color: "#CCB2D5", 
            }
        },

        //Change color when the menu is OPEN
        "& .MuiAutocomplete-popupIndicatorOpen": {
            color: "#F8F5FE", 
        },

        
        "& .Mui-error": {
            color: "#FF5555 !important", 
        
            "&.MuiInputLabel-root": {
                color: "#FF5555 !important",
            },

            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF5555",
            },
        
        },

        "& .MuiFormHelperText-root": {
            color: "#FF5555",
            marginLeft: 0,
            fontSize: "12px",
        }
    }

    const switchStyle = {
    
        '& .MuiSwitch-track': {
            backgroundColor: '#302C3F',
            opacity: 1, 
        },

        '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#ffffff', 
            '& + .MuiSwitch-track': {
            backgroundColor: '#8B579E',
            opacity: 1,
            },
        },
    };

    //This style is for the textbox component of the Calendar
    const datePickerStyle = {
        width: "476px",
        color:"#F8F5FE",
        backgroundColor: "#1C1A25",
        overflow: "hidden !important",
        borderRadius:"16px",
        height:"56px",

        
        "& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root": {
            height: "56px",
            backgroundColor: "#1C1A25",
            borderRadius: "16px",
            color: "#F8F5FE",

            "& .MuiPickersOutlinedInput-notchedOutline": {
                borderColor: "#302C3F",
                borderWidth: "1px",
                color: "#F8F5FE", 
            },
            "&:hover .MuiPickersOutlinedInput-notchedOutline": {
                borderColor: "#302C3F",
                borderWidth: "1px",
            },
            // Hover State
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#302C3F",
                borderWidth: "1px",
                backgroundColor:"#302C3F",
            },
            // Focused State
            "&.Mui-focused fieldset": {
                borderColor: "#CCB2D5 !important", 
                borderWidth: "1px !important",     
                
                
            },
             "&.Mui-focused ": {
                borderColor: "#CCB2D5 !important", 
                borderWidth: "1px !important",     
                backgroundColor:"#302C3F",
                
            },
        },

        "& .MuiOutlinedInput-input": {
            color: "#F8F5FE", 
            overflow: "hidden"
            
        },

        "& .MuiInputLabel-root": {
            color: "#F8F5FE",
            "&.Mui-focused": {
                color: "#F8F5FE",
            },
        },

        //Calendar Icon
        "& .MuiSvgIcon-root": {
            color: "#686673",
            overflow: "hidden"
        },
        "&.Mui-focused .MuiSvgIcon-root": {
            color: "#F8F5FE", 
        }
    };

    //This style is for the calendar component of the Calendar
    const calendarComponentStyle = {
        backgroundColor: "#1C1A25",
        color: "#F8F5FE",
        border: "1px solid #3C374F",
        borderRadius: "16px",
        overflow:"hidden",

        "& .MuiPickersCalendarHeader-root": {
        position: "relative", 
        display: "flex",
        alignItems: "center",
        justifyContent: "center", 
    },

    "& .MuiPickersCalendarHeader-labelContainer": {
        zIndex: 1,
        margin: "0", 
    },

    "& .MuiPickersArrowSwitcher-root": {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "space-between", 
        padding: "0 12px", 
        pointerEvents: "auto", 
    },

    "& .MuiPickersArrowSwitcher-spacer": {
        display: "none",
    },

    "& .MuiPickersArrowSwitcher-button": {
        color: "#F8F5FE",
        pointerEvents: "auto",
    },
    

        "& .MuiDateCalendar-root": {
            height:"auto !important",
            maxHeight: "none",
            width:"auto !important",
            backgroundColor: "#302C3F", 
        },

        "& .MuiDayCalendar-slideTransition": {
            minHeight:'200px !important',
            height: 'auto !important', 
        },


        
        // Days of week  
        "& .MuiDayCalendar-weekDayLabel": {
            color: "#F8F5FE",
        },
        // The actual dates 
        "& .MuiPickersDay-root": {
            color: "#F8F5FE",
            "&:hover": {
                        backgroundColor:"#CCB2D5"
                        
                    },
            //Selected Day
            "&.Mui-selected": {
                backgroundColor: "#B12F6B", 
                color: "#F8F5FE", 
            
                "&:hover": {
                    backgroundColor: "#CCB2D5", 
                },
            
                "&:focus": {
                    backgroundColor: "#B12F6B !important",
                }
            },
            "&.MuiPickersDay-today": {
                borderColor: "#CCB2D5 !important",
            }
        },
        // The Month/Year Switcher Icons
        "& .MuiPickersCalendarHeader-switchViewIcon": {
            color: "#F8F5FE",
        },
        // The Arrow Icons
        "& .MuiPickersArrowSwitcher-button": {
            color: "#F8F5FE",
        },

        "& .MuiDayCalendar-weekDayLabel": {
        color: "#686673 !important", 
        fontSize: "14px",          
        },

        "& .MuiPickersDay-dayOutsideMonth": {
            color: "#575560 !important", 
            
        },
        

            "& .MuiMonthCalendar-button":{
                    "&:hover": {
                        backgroundColor:"#CCB2D5"
                        
                    },
                    "&.Mui-selected": {
                    backgroundColor: "#B12F6B !important",
                    
                        "&:hover": {
                            backgroundColor: "#CCB2D5 !important",
                        }
                    }
                },

                "& .MuiYearCalendar-button":{
                    "&:hover": {
                        backgroundColor:"#CCB2D5"
                        
                    },
                    "&.Mui-selected": {
                    backgroundColor: "#B12F6B !important",
                    
                        "&:hover": {
                            backgroundColor: "#CCB2D5 !important",
                        }
                    }
                }
    }

    //This style is for the dropdown menus
    const dropDownStyle = {
        // The Container 
        backgroundColor: "#1C1A25", 
        color: "#F8F5FE",
        border: "1px solid #302C3F",
        borderRadius: "16px", 
        marginTop: "8px",     
        
        //The List Items 
        "& .MuiAutocomplete-option": {
            padding: "12px 16px", 
            borderRadius:"16px",
            
            "&:hover": {
                backgroundColor: "#302C3F", 
            },

            "&[aria-selected='true']": {
                backgroundColor: "#CCB2D5", 
                color: "#1C1A25", 
                
                "&:hover": {
                    backgroundColor: "#B099B9",
                }
            },

            "&.Mui-focused": {
                backgroundColor: "#302C3F", 
            }
        },

        "& .MuiAutocomplete-noOptions": {
            color: "#686673",
            backgroundColor: "#1C1A25"
        }
    }

    //This style is for the radio buttons
    const radioButtonStyle ={
        color: "#686673", 
            '&.Mui-checked': {
                color: "#B099B9",
            } 
    }

    //This style is for multiline textfields
    const multilineTextboxStyle = {
    width: "476px",
    borderRadius: "16px",

    "& .MuiOutlinedInput-root": {
        backgroundColor: "#1C1A25",
        color: "#F8F5FE",
        borderRadius: "16px",
        
        height: "auto",          
        minHeight: "52px",       
        padding: "12px",         
        alignItems: "flex-start",

        '& fieldset': {
            borderColor: '#302C3F',
            borderWidth: '1px',
        },

        '&:hover fieldset': {
            borderColor: '#302C3F',
            borderWidth: '1px'
        },

        '&.Mui-focused fieldset': {
            borderColor: '#302C3F',
            borderWidth: '1px'
        },

        "&.Mui-disabled": {
            backgroundColor: "#13111B !important",
            color: "#F8F5FE !important",
            opacity: "1 !important",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#302C3F !important",
                borderStyle: "solid !important",
            },
            "& .MuiOutlinedInput-input": {
                color: "#fef5f5ff !important",
                "WebkitTextFillColor": "#F8F5FE !important",
            },
        },
    },

    // Inside Text
    "& .MuiInputBase-input": {
        color: "#F8F5FE",
        padding: "0px", 
    },

    '& .MuiInputLabel-root': {
        color: '#F8F5FE',
        '&.Mui-focused': { color: '#F8F5FE' },
    },

    "& .Mui-error": {
        color: "#FF5555 !important",

        "&.MuiInputLabel-root": {
            color: "#FF5555 !important",
        },

        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FF5555",
        },
    },

    // Helper Text 
    "& .MuiFormHelperText-root": {
        color: "#FF5555",
        marginLeft: 0,
        fontSize: "12px",
    }
};

    //This is the section that has the lists that get loaded into other components
    const RDARegions = [
        {label:"N/A - RDA Alumni", value:"RDAAlumni"},
        {label:"RDA/MidStates", value:"MidStates"},
        {label:"RDA/Northeast", value:"Northeast"},
        {label:"RDA/Pacific", value:"Pacific"},
        {label:"RDA/Southeast", value:"Southeast"},
        {label:"RDA/Southwest", value:"Southwest"}
    ]

    //This is the list of RDA Member Companies
    const RDAMemberCompanies = [
        { label: "N/A - RDA Alumni", value: "RDAAlumni" },
        { label: "Alabama Dance Theatre", value: "AlabamaDanceTheatre" },
        { label: "Appalachian Ballet Company", value: "AppalachianBalletCompany" },
        { label: "ARC Youth Dance Company", value: "ARCYouthDanceCompany" },
        { label: "Arkansas Festival Ballet", value: "ArkansasFestivalBallet" },
        { label: "Atlanta Dance Theatre", value: "AtlantaDanceTheatre" },
        { label: "Ballet America", value: "BalletAmerica" },
        { label: "Ballet Aviv", value: "BalletAviv" },
        { label: "Ballet Eclectica", value: "BalletEclectica" },
        { label: "Ballet Southeast", value: "BalletSoutheast" },
        { label: "BalletForte", value: "BalletForte" },
        { label: "Baton Rouge Ballet Theatre", value: "BatonRougeBalletTheatre" },
        { label: "California Dance Ensemble", value: "CaliforniaDanceEnsemble" },
        { label: "Canton Ballet", value: "CantonBallet" },
        { label: "Carolina Civic Ballet", value: "CarolinaCivicBallet" },
        { label: "City Ballet of Houston", value: "CityBalletOfHouston" },
        { label: "Collin County Ballet Theatre", value: "CollinCountyBalletTheatre" },
        { label: "Corpus Christi Ballet", value: "CorpusChristiBallet" },
        { label: "Dallas Ballet Company", value: "DallasBalletCompany" },
        { label: "Dance Wisconsin", value: "DanceWisconsin" },
        { label: "Dayton Dance Conservatory Company", value: "DaytonDanceConservatoryCompany" },
        { label: "Delaware Ballet", value: "DelawareBallet" },
        { label: "Delaware Dance Company", value: "DelawareDanceCompany" },
        { label: "Eisenhower Dance Detroit Youth Ensemble", value: "EisenhowerDanceDetroitYouthEnsemble" },
        { label: "Elevations Studio Company", value: "ElevationsStudioCompany" },
        { label: "Encore", value: "Encore" },
        { label: "Erie Contemporary Ballet Theatre", value: "ErieContemporaryBalletTheatre" },
        { label: "Études Ballet Company", value: "EtudesBalletCompany" },
        { label: "Festival Ballet Albuquerque", value: "FestivalBalletAlbuquerque" },
        { label: "Forum Dance Theatre", value: "ForumDanceTheatre" },
        { label: "Georgia Metropolitan Dance Theatre", value: "GeorgiaMetropolitanDanceTheatre" },
        { label: "Greater Annapolis Ballet", value: "GreaterAnnapolisBallet" },
        { label: "Greater Lansing Ballet", value: "GreaterLansingBallet" },
        { label: "Greensboro Ballet", value: "GreensboroBallet" },
        { label: "High Point Ballet", value: "HighPointBallet" },
        { label: "Huntingdon Regional Ballet", value: "HuntingdonRegionalBallet" },
        { label: "Juxtapose Arts Collective", value: "JuxtaposeArtsCollective" },
        { label: "Kansas Ballet", value: "KansasBallet" },
        { label: "Kingwood Ballet", value: "KingwoodBallet" },
        { label: "Lawrence Ballet Theatre", value: "LawrenceBalletTheatre" },
        { label: "Louisiana Delta Ballet", value: "LouisianaDeltaBallet" },
        { label: "Louisville Ballet Youth Ensemble", value: "LouisvilleBalletYouthEnsemble" },
        { label: "Mendocino Ballet", value: "MendocinoBallet" },
        { label: "Midland Festival Ballet", value: "MidlandFestivalBallet" },
        { label: "Mississippi Metropolitan Ballet", value: "MississippiMetropolitanBallet" },
        { label: "Nevada Dance Company", value: "NevadaDanceCompany" },
        { label: "New Paradigm Dance Theater", value: "NewParadigmDanceTheater" },
        { label: "The Performing Company", value: "ThePerformingCompany" },
        { label: "The Perla Ballet", value: "ThePerlaBallet" },
        { label: "Philadelphia Dance Theatre", value: "PhiladelphiaDanceTheatre" },
        { label: "Plano Metropolitan Ballet", value: "PlanoMetropolitanBallet" },
        { label: "Providence Creek Ballet Theater", value: "ProvidenceCreekBalletTheater" },
        { label: "Raleigh Dance Theatre", value: "RaleighDanceTheatre" },
        { label: "Rise City Dance", value: "RiseCityDance" },
        { label: "Roswell Dance Theatre", value: "RoswellDanceTheatre" },
        { label: "Sac Civic Ballet", value: "SacCivicBallet" },
        { label: "Saratoga Springs Youth Ballet", value: "SaratogaSpringsYouthBallet" },
        { label: "South Dayton Dance Theatre", value: "SouthDaytonDanceTheatre" },
        { label: "Southeast Alabama Dance Company", value: "SoutheastAlabamaDanceCompany" },
        { label: "Southern Ballet Theatre", value: "SouthernBalletTheatre" },
        { label: "Southern New Hampshire Youth Ballet", value: "SouthernNewHampshireYouthBallet" },
        { label: "Terminus Student Company", value: "TerminusStudentCompany" },
        { label: "Twin City Ballet Company", value: "TwinCityBalletCompany" },
        { label: "Victoria Ballet Theatre", value: "VictoriaBalletTheatre" },
        { label: "Western Arkansas Ballet", value: "WesternArkansasBallet" },
        { label: "Yuba City Youth Ballet", value: "YubaCityYouthBallet" }
    ];
    
    const sizes =[
        {value: 'small', label: "Adult Small"},
        {value: 'medium', label: "Adult Medium"},
        {value: 'large', label: "Adult Large"},
        {value: 'xlarge', label:"Adult XL"}
    ]

    const merch=[
        "Dance & Choreography Intensive T-Shirt ($30 each)", 
        "Dance & Choreography Intensive Sweatshirt ($50 each)", 
        "Preformance Workshop T-Shirt ($30 each)", 
        "Preformance Workshop Sweatshirt ($50 each)" 
    ]

    const DCImerch=[
        {   
            label:"Dance & Choreography Intensive T-Shirt ($30 each)", 
            id:"DCITShirt"
        },
        {
            label:"Dance & Choreography Intensive Sweatshirt ($50 each)", 
            id:"DCISweatShirt"
        }
    ]

    const PWmerch=[
        {   
            label:"Preformance Workshop T-Shirt ($30 each)", 
            id:"PWTShirt"
        },
        {
            label:"Preformance Workshop Sweatshirt ($50 each)", 
            id:"PWSweatShirt"
        } 
    ]

    const pronouns=[
        {value: 'she/her', label: "she/her"},
        {value: 'he/him', label: "he/him"},
        {value: 'they/them', label: "they/them"},
        {value: 'she/they', label: "she/they"},
        {value: 'he/they', label: "he/they"},
        {value: 'preferNot', label: "Prefer not to say"},
    ]

    const genders=[
        {value: 'female', label: "Female"},
        {value: 'male', label: "Male"},
        {value: 'nonbinary', label: "Non-binary"},
        {value: 'preferNot', label: "Prefer not to say"},
    ]

    const events=[
        {title:"Dance & Choreography Intensive (“DCI”)", location:"Roswell, Georgia", date:"June 8 - 13, 2026", id:"DCIRosewell26", options:[{value: "Dancer", label:"Dancer"}, { value: "Choreographer", label: "Choreographer", subtext: "(by acceptance only)" }, ]},
        {title:"Dance & Choreography Intensive (“DCI”)", location:"Lawrence, Kansas", date:"June 22 - 27, 2026", id:"DCILawrence26", options:[{value: "Dancer", label:"Dancer"}, { value: "Choreographer", label: "Choreographer", subtext: "(by acceptance only)" }, ]},
        {title:"Dance & Choreography Intensive (“DCI”)", location:"Carlisle, Pennsylvania", date:"July 27 - August 1, 2026", id:"DCICarlisle26", options:[{value: "Dancer", label:"Dancer"}, { value: "Choreographer", label: "Choreographer", subtext: "(by acceptance only)" }, ]},
        {title:"Performance Workshop", location:"Kingwood, Texas", date:"June 5 - 7, 2026", id:"PWKingwood26", options:[{value: "Dancer", label:"Dancer"},]},
    ]

    const medicalConditions =[
        "Seizure Disorder",
        "Diabetes",
        "Allergies with Anaphylaxis Reactions",
        "Asthma"
    ]

    const [selectionError, setSelectionError] = useState(false);
    //This is for loading just the tickets for the event that is signed up
    const anyEventTickets = DCIRosewell26watch?.trim() !== ""||DCILawrence26watch?.trim() !== ""||DCICarlisle26watch?.trim() !== "";

    //This sets the amount of authorized individuals who can pick up the dancer
    const [personCount, setPersonCount] = useState(1);

    //This is the variable for the dancer signature on the last page
    const canvasRef = useRef(null);
    const clearCanvas = () => {
        canvasRef.current.clearCanvas();
    }

    //This is the signature for gaurdian/parent on last page
    const guardianFinalSign = useRef(null);

    //These are the signatures for the Health and Saftey Page
    const dancerHealthSign = useRef(null);
    const guardianHealthSign = useRef(null);

    const [canvasKey, setCanvasKey] = useState(0);

    //This is for having the stroke color be different on the webpage and on the pdf
    const [strokeColor, setStrokeColor] = useState("#F8F5FE");
    useEffect(() => {
    const timer = setTimeout(() => {
        setCanvasKey(prev => prev + 1);
    }, 150); 
    return () => clearTimeout(timer);
}, []);

    // console.log(watch("dateOfBirth"))

    const handleSavePdf = async () => {
    try {
        const signatureImageBase64 = await canvasRef.current.exportImage("png");
        const existingPdfBytes = await fetch('/path/to/document.pdf').then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const pngImage = await pdfDoc.embedPng(signatureImageBase64);

        //This is where the the pdf is getting written to on the pages
        const pages = pdfDoc.getPages();
        const firstPage = pages[0];
        const { width, height } = pngImage.scale(0.5); 

        firstPage.drawImage(pngImage, {
            x: 50,
            y: 150, 
            width: width,
            height: height,
        });

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'signed_waiver.pdf';
        link.click();

    } catch (e) {
        console.error("Error signing PDF", e);
    }
    };

   const handleSeeAgreement = async (fileName) => {
    try {
        if (!canvasRef.current) return;
        // setStrokeColor("#1C1A25");
        // await new Promise(resolve => setTimeout(resolve, 50));

        const signatureImageBase64 = await canvasRef.current.exportImage("png");

        const response = await fetch(`/${fileName}`)
        if (!response.ok) throw new Error(`Could not find ${fileName} in public folder`);
        const existingPdfBytes = await response.arrayBuffer();

       const pdfDoc = await PDFDocument.load(existingPdfBytes); 
        
        // setStrokeColor("#F8F5FE");

        if (!signatureImageBase64) {
            alert("Please provide a signature first.");
            return;
        }
        let parentSignature = null
        let parentPngImage = null
        if(guardianFinalSign.current){
            parentSignature =await guardianFinalSign.current.exportImage("png")
            parentPngImage = await pdfDoc.embedPng(parentSignature);
        };

        if (calculatedAge<18 && !parentSignature){
            alert("Please provide a signature first.")
        }


        // const response = await fetch('/2026RDASummerDanceProgramsParticipantReleaseForm.pdf');
        
        
        const pngImage = await pdfDoc.embedPng(signatureImageBase64);
        
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const participantName = String(getValues("printedSignName") || "")
        const participantAddress = String(getValues("participantStreetAddress") ||"")
        const participantCSZ = String(getValues("participantCityStateZipcode") || "")


        const parentName = String(getValues("parentSignName")|| "")
        const parentAddress = String(getValues("parentStreetAddress") || "")
        const parentCSZ = String(getValues("parentCityStateZipcode") || "")
        

        const now = dayjs().format("MM-DD-YYYY");
        
        const pages = pdfDoc.getPages();
        const firstPage = pages[0]
        const signPage = pages[2];
        
        const dims = pngImage.scale(0.5); 
        firstPage.fontSize=10
        signPage.fontSize=10
        
        let fontSize = 10
        
        
        //The Kingwood file is considerably different so the pdf locations need to be changed
        if (fileName === "2026KingwoodWaiver.pdf" ){
            //first page info
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


            //last page info
            signPage.drawImage(pngImage, {
                x: 175,
                y: 485, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:469,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 454,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 440, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 426, 
                size:fontSize
            });
            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 334, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 320,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 305, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 290, 
                size:fontSize
            });
        }




        }
        else if (fileName === "2026RosewellWaiver.pdf" ){
            //first page info
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


            //last page info
            signPage.drawImage(pngImage, {
                x: 175,
                y: 515, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:499,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 484,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 470, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 456, 
                size:fontSize
            });

            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 364, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 350,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 335, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 320, 
                size:fontSize
            });
        }




        }
        //The rest of the waivers
        else{ 
            
            
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


           signPage.drawImage(pngImage, {
                x: 175,
                y: 528, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:512,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 497,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 483, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 469, 
                size:fontSize
            });

            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 377, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 363,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 348, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 333, 
                size:fontSize
            });
        }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        window.open(url, '_blank');

    } catch (err) {
        console.error("PDF Generation Error:", err);
        alert("Error: " + err.message);
    }
    };

    const handleDownloadAgreement = async (fileName) => {
    try {
        if (!canvasRef.current) return;

        const signatureImageBase64 = await canvasRef.current.exportImage("png");

        const response = await fetch(`/${fileName}`)
        if (!response.ok) throw new Error(`Could not find ${fileName} in public folder`);
        const existingPdfBytes = await response.arrayBuffer();

       const pdfDoc = await PDFDocument.load(existingPdfBytes); 
        

        if (!signatureImageBase64) {
            alert("Please provide a signature first.");
            return;
        }
        let parentSignature = null
        let parentPngImage = null
        if(guardianFinalSign.current){
            parentSignature =await guardianFinalSign.current.exportImage("png")
            parentPngImage = await pdfDoc.embedPng(parentSignature);
        };

        if (calculatedAge<18 && !parentSignature){
            alert("Please provide a signature first.")
        }


        // const response = await fetch('/2026RDASummerDanceProgramsParticipantReleaseForm.pdf');
        
        
        const pngImage = await pdfDoc.embedPng(signatureImageBase64);
        
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const participantName = String(getValues("printedSignName") || "")
        const participantAddress = String(getValues("participantStreetAddress") || "")
        const participantCSZ = String(getValues("participantCityStateZipcode") || "")


        const parentName = String(getValues("parentSignName") || "")
        const parentAddress = String(getValues("parentStreetAddress") || "")
        const parentCSZ = String(getValues("parentCityStateZipcode") || "")
        

        const now = dayjs().format("MM-DD-YYYY");
        
        const pages = pdfDoc.getPages();
        const firstPage = pages[0]
        const signPage = pages[2];
        
        const dims = pngImage.scale(0.5); 
        firstPage.fontSize=10
        signPage.fontSize=10
        
        let fontSize = 10
        
        
        //The Kingwood file is considerably different so the pdf locations need to be changed
        if (fileName === "2026KingwoodWaiver.pdf" ){
            //first page info
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


            //last page info
            signPage.drawImage(pngImage, {
                x: 175,
                y: 485, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:469,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 454,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 440, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 426, 
                size:fontSize
            });
            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 334, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 320,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 305, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 290, 
                size:fontSize
            });
        }




        }
        else if (fileName === "2026RosewellWaiver.pdf" ){
            //first page info
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


            //last page info
            signPage.drawImage(pngImage, {
                x: 175,
                y: 515, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:499,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 484,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 470, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 456, 
                size:fontSize
            });

            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 364, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 350,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 335, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 320, 
                size:fontSize
            });
        }




        }
        //The rest of the waivers
        else{ 
            
            
            firstPage.drawText(now,{
                x: 444,
                y: 592, 
            })

            let textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 175) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }

            firstPage.drawText(participantName, {
                x: 60,
                y: 577,
                size: fontSize, 
            });


           signPage.drawImage(pngImage, {
                x: 175,
                y: 528, 
                width: dims.width*.25,
                height: dims.height*.25,
            });
            signPage.drawText(String(calculatedAge),{
                x:325,
                y:512,
            })

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
            }
            signPage.drawText(participantName, {
                x: 130,
                y: 497,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
            }
            signPage.drawText(participantAddress, {
                x: 120,
                y: 483, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
            }

            signPage.drawText(participantCSZ, {
                x: 135,
                y: 469, 
                size:fontSize
            });

            if(calculatedAge<18){
            signPage.drawImage(parentPngImage, {
                x: 195,
                y: 377, 
                width: dims.width*.25,
                height: dims.height*.25,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentName, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentName, fontSize);
            }
            signPage.drawText(parentName, {
                x: 130,
                y: 363,
                size: fontSize, 
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            while (textWidth > 265) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
            }
            signPage.drawText(parentAddress, {
                x: 120,
                y: 348, 
                size:fontSize,
            });

            fontSize = 10
            textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            while (textWidth > 260) {
                fontSize -= 0.5;
                textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
            }

            signPage.drawText(parentCSZ, {
                x: 135,
                y: 333, 
                size:fontSize
            });
        }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `Signed_${fileName}`; 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const signedFile = new File([blob], `Signed_${fileName}`, { type: 'application/pdf' });
        setValue("signedPdfFile", signedFile);

    } catch (err) {
        console.error("PDF Generation Error:", err);
        alert("Error: " + err.message);
    }
    };

    const generatePdfFile = async (fileName) => {
        try {
            if (!canvasRef.current) return;
            const signatureImageBase64 = await canvasRef.current.exportImage("png");
            const response = await fetch(`/${fileName}`)
            if (!response.ok) throw new Error(`Could not find ${fileName} in public folder`);
            const existingPdfBytes = await response.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            if (!signatureImageBase64) {
                alert("Please provide a signature first.");
                return;
            }
            let parentSignature = null
            let parentPngImage = null
            if (guardianFinalSign.current) {
                parentSignature = await guardianFinalSign.current.exportImage("png")
                parentPngImage = await pdfDoc.embedPng(parentSignature);
            };
            if (calculatedAge < 18 && !parentSignature) {
                alert("Please provide a signature first.")
            }
            const pngImage = await pdfDoc.embedPng(signatureImageBase64);
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const participantName = String(getValues("printedSignName") || "")
            const participantAddress = String(getValues("participantStreetAddress") || "")
            const participantCSZ = String(getValues("participantCityStateZipcode") || "")
            const parentName = String(getValues("parentSignName") || "")
            const parentAddress = String(getValues("parentStreetAddress") || "")
            const parentCSZ = String(getValues("parentCityStateZipcode") || "")
            const now = dayjs().format("MM-DD-YYYY");
            const pages = pdfDoc.getPages();
            const firstPage = pages[0]
            const signPage = pages[2];
            const dims = pngImage.scale(0.5);
            firstPage.fontSize = 10
            signPage.fontSize = 10
            let fontSize = 10
            if (fileName === "2026KingwoodWaiver.pdf") {
                firstPage.drawText(now, {
                    x: 444,
                    y: 592,
                })
                let textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 175) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                firstPage.drawText(participantName, {
                    x: 60,
                    y: 577,
                    size: fontSize,
                });
                signPage.drawImage(pngImage, {
                    x: 175,
                    y: 485,
                    width: dims.width * .25,
                    height: dims.height * .25,
                });
                signPage.drawText(String(calculatedAge), {
                    x: 325,
                    y: 469,
                })
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                signPage.drawText(participantName, {
                    x: 130,
                    y: 454,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                }
                signPage.drawText(participantAddress, {
                    x: 120,
                    y: 440,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                while (textWidth > 260) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                }
                signPage.drawText(participantCSZ, {
                    x: 135,
                    y: 426,
                    size: fontSize
                });
                if (calculatedAge < 18) {
                    signPage.drawImage(parentPngImage, {
                        x: 195,
                        y: 334,
                        width: dims.width * .25,
                        height: dims.height * .25,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    }
                    signPage.drawText(parentName, {
                        x: 130,
                        y: 320,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    }
                    signPage.drawText(parentAddress, {
                        x: 120,
                        y: 305,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    while (textWidth > 260) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    }
                    signPage.drawText(parentCSZ, {
                        x: 135,
                        y: 290,
                        size: fontSize
                    });
                }
            }
            else if (fileName === "2026RosewellWaiver.pdf") {
                firstPage.drawText(now, {
                    x: 444,
                    y: 592,
                })
                let textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 175) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                firstPage.drawText(participantName, {
                    x: 60,
                    y: 577,
                    size: fontSize,
                });
                signPage.drawImage(pngImage, {
                    x: 175,
                    y: 515,
                    width: dims.width * .25,
                    height: dims.height * .25,
                });
                signPage.drawText(String(calculatedAge), {
                    x: 325,
                    y: 499,
                })
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                signPage.drawText(participantName, {
                    x: 130,
                    y: 484,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                }
                signPage.drawText(participantAddress, {
                    x: 120,
                    y: 470,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                while (textWidth > 260) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                }
                signPage.drawText(participantCSZ, {
                    x: 135,
                    y: 456,
                    size: fontSize
                });
                if (calculatedAge < 18) {
                    signPage.drawImage(parentPngImage, {
                        x: 195,
                        y: 364,
                        width: dims.width * .25,
                        height: dims.height * .25,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    }
                    signPage.drawText(parentName, {
                        x: 130,
                        y: 350,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    }
                    signPage.drawText(parentAddress, {
                        x: 120,
                        y: 335,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    while (textWidth > 260) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    }
                    signPage.drawText(parentCSZ, {
                        x: 135,
                        y: 320,
                        size: fontSize
                    });
                }
            }
            else {
                firstPage.drawText(now, {
                    x: 444,
                    y: 592,
                })
                let textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 175) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                firstPage.drawText(participantName, {
                    x: 60,
                    y: 577,
                    size: fontSize,
                });
                signPage.drawImage(pngImage, {
                    x: 175,
                    y: 528,
                    width: dims.width * .25,
                    height: dims.height * .25,
                });
                signPage.drawText(String(calculatedAge), {
                    x: 325,
                    y: 512,
                })
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantName, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantName, fontSize);
                }
                signPage.drawText(participantName, {
                    x: 130,
                    y: 497,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                while (textWidth > 265) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantAddress, fontSize);
                }
                signPage.drawText(participantAddress, {
                    x: 120,
                    y: 483,
                    size: fontSize,
                });
                fontSize = 10
                textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                while (textWidth > 260) {
                    fontSize -= 0.5;
                    textWidth = font.widthOfTextAtSize(participantCSZ, fontSize);
                }
                signPage.drawText(participantCSZ, {
                    x: 135,
                    y: 469,
                    size: fontSize
                });
                if (calculatedAge < 18) {
                    signPage.drawImage(parentPngImage, {
                        x: 195,
                        y: 377,
                        width: dims.width * .25,
                        height: dims.height * .25,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentName, fontSize);
                    }
                    signPage.drawText(parentName, {
                        x: 130,
                        y: 363,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    while (textWidth > 265) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentAddress, fontSize);
                    }
                    signPage.drawText(parentAddress, {
                        x: 120,
                        y: 348,
                        size: fontSize,
                    });
                    fontSize = 10
                    textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    while (textWidth > 260) {
                        fontSize -= 0.5;
                        textWidth = font.widthOfTextAtSize(parentCSZ, fontSize);
                    }
                    signPage.drawText(parentCSZ, {
                        x: 135,
                        y: 333,
                        size: fontSize
                    });
                }
            }
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            return new File([blob], `Signed_${fileName}`, { type: 'application/pdf' });
        } catch (err) {
            console.error("PDF Generation Error:", err);
            alert("Error: " + err.message);
        }
    };
    
    const handleSubmitAgreement = async () => {
        const filesToUpload = [];

        if (DCIRosewell26watch && DCIRosewell26watch.trim() !== "") {
            const file = await generatePdfFile("2026RosewellWaiver.pdf");
            if (file) filesToUpload.push(file);
        }

        if (DCILawrence26watch && DCILawrence26watch.trim() !== "") {
            const file = await generatePdfFile("2026LawrenceWaiver.pdf");
            if (file) filesToUpload.push(file);
        }
         if (DCICarlisle26watch && DCICarlisle26watch.trim() !== "") {
            const file = await generatePdfFile("2026CarlisleWaiver.pdf");
            if (file) filesToUpload.push(file);
        }

        if (PWKingwood26watch && PWKingwood26watch.trim() !== "") {
            const file = await generatePdfFile("2026KingwoodWaiver.pdf");
            if (file) filesToUpload.push(file);
        }


        return filesToUpload;
    };

    const handleRemovePerson = (idToRemove, index) => {
        if (personIds.length > 1) {
            unregister(`authorizedName${index}`);
            unregister(`authorizedRelationship${index}`);
            unregister(`authorizedPhone${index}`);
            unregister(`authorizedEmail${index}`);

            setPersonIds(prev => prev.filter(id => id !== idToRemove));
        }
    };

    const [personIds, setPersonIds] = useState([Date.now()]);
    
    const handleAddPerson = () => {
        setPersonIds(prev => [...prev, Date.now()]);
    };
    

    return (
        <div className={styles.background} style={{ paddingBottom: '0px',paddingTop:'0px', overflow: 'hidden' }}>
            <div className={styles.mainBox}>
                <div className={styles.leftSide}>
                    <div className={styles.topLeft}>
                        <Box sx={{ width: '100%', borderStyle:"solid", borderColor:"#232226",borderWidth:"0 1px 1px 0" }}>
                            <LinearProgressWithLabel value={progress} />
                        </Box>
                    </div>
                    <div className={styles.midLeft}>

                        <Box sx={progressStyle}>
                            <Stepper activeStep={activeStep} orientation="vertical" connector={null}>
                                {steps.map((step, index) => {
                                    const isActive = activeStep === index;
                                return(
                                    <Step key={step.label} id={`step-${index}`} expanded={true}>
                                        <StepLabel  sx={{...stepsStyle, backgroundColor: isActive ? '#B12F6B' : '#13111B','& .MuiStepLabel-iconContainer': {display: 'none',},}}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Avatar
                                                    sx={{
                                                    bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.1)',
                                                    // border: isActive ? 'none' : '1px solid #555',
                                                    color: 'white',
                                                    mr: 2,
                                                    }}
                                                >
                                                    {step.icon}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{height:"16px", color:isActive ? '#CDCCD0' :'#686673', fontSize:"14px"}}>{step.label}</Typography>
                                                    <Typography sx={{height:"20px", color:'#F8F5FE', fontSize:"16px"}}>{step.description}</Typography>
                                                </Box>
                                            </Box>
                                        </StepLabel>
                                    </Step>
                                );})}
                            </Stepper>

                            {/* {activeStep === steps.length && (
                                <Paper square elevation={0} sx={{ p: 3 }}>
                                <Typography>All steps completed - you&apos;re finished</Typography>
                                <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                                    Reset
                                </Button>
                                </Paper>
                            )} */}
                        </Box>

                    </div>
                    <div className={styles.botLeft}>
                        <Box sx={{borderStyle:"solid", borderColor:"#232226",borderWidth:" 1px 1px 0 0", height: 74, display:'flex', justifyContent: 'center', paddingTop:"12px", width:'358px' }}>
                            <Button variant="contained" sx={exitButtonStyle} component={Link} to="/accessCode"> Exit Profile</Button>
                        </Box>
                    </div>
                </div>
                <div className={styles.RightSide}>
                    <div className={styles.topRight}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: "100%", height:93, borderStyle:"solid", borderColor:"#232226",borderWidth:"0 0 1px 0", paddingLeft:"16px"   }}>
                            <Avatar
                                sx={{
                                    width:64,
                                    height:64,
                                    bgcolor:'rgba(255,255,255,0.2)',
                                    border:'none',
                                    color: 'white',
                                    mr: 2,
                                }}
                            >
                                {steps[activeStep].icon}
                            </Avatar>
                            <Box>
                                <Typography sx={{height:20, fontSize:'16px', color:'#CDCCD0'}}>{steps[activeStep].label} of 10</Typography>
                                <Typography sx={{height:32, fontSize:"28px", color:'#F8F5FE',}}>{steps[activeStep].description}</Typography>
                            </Box>
                        </Box>
                    </div>
                    <div className={styles.midRight} style={{width:"100%"}}>
                        <Box sx={{...midRightStyle, justifySelf:"center" }}>
                            {/* height 1077 */}
                        <form id="PersonalInfo" onSubmit={handleSubmit(onSubmit, onError)} sx={{display:"flex"}}>
                            {/*STEP 1: Personal Info*/}
                            {activeStep === 0 &&(
                                <Grid container spacing={2} sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    {/* <div className={styles.labelText}>Full Legal Name</div>
                                    <TextField sx={textboxStyle} placeholder="example" {...register("example")}/> */}
                                    <SectionDescription title="Participant Information" description = "Please provide the following information about the program participant. If the participant is under 13 years of age, a guardian must provide the guardian’s email address and phone number."/>
                                    <Grid item>
                                        <LabeledInput label="Full Legal Name" placeholder="Enter your full name" register={register} name="fullName"  sx={textboxStyle} error={errors.fullName} rules={{ required:"Full name is required " }}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Preferred Name" placeholder="Enter your preferred name" register={register} name="preferredName" rules={{ required: false }} sx={textboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledCalendarInput label="Date of Birth" placeholder="mm/dd/yy" control={control} name="dateOfBirth" rules={{ required: true }} sx={datePickerStyle} sx2={calendarComponentStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Age" disabled placeholder="Calculated automatically" register={register} name="age" rules={{ required: false }} sx={textboxStyle} value={calculatedAge}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Gender" placeholder="Enter your gender" register={register} name="gender" error={errors.gender} rules={{ required:"Gender is required" }} sx={textboxStyle}/>
                                        {/* <BasicListField label="Gender" placeholder="Select your gender" control={control} name="gender" rules={{ required: true }} textfieldSx={textboxStyle} dropDownSx={dropDownStyle} options={genders}/> */}
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Pronouns (optional)" placeholder="e.g., she/her, he/him, they/them" register={register} name="prounouns" rules={{ required: true }} sx={textboxStyle}/>
                                        {/* <BasicListField label="Pronouns (optional)" placeholder="Select pronouns" control={control} name="pronouns" rules={{ required: true }} textfieldSx={textboxStyle} dropDownSx={dropDownStyle} options={pronouns}/> */}
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Email" placeholder="name@example.com" register={register} name="dancerEmail" rules={{ required: "Invalid email format. Use: name@email.com", pattern:{value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Please enter a valid email address" } }} error= {errors.dancerEmail} sx={textboxStyle} slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <MailOutlinedIcon 
                                                            sx={{ color:"#686673" }} 
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }
                                        }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        {/* <LabeledInput label="Phone Number" placeholder="Enter your phone number" register={register} name="phoneNumber" rules={{ required: true }} sx={textboxStyle}/> */}
                                        <LabeledPhoneInput label="Phone Number" control={control} name="dancerPhoneNumber" sx={textboxStyle}  phoneMask={phoneMask} 
                                        rules={{required:"Phone number is required", validate:(value) =>{
                                            if (!value || value.length !== 10) {
                                                return "Please enter all required digits";
                                            }
                                            return true;
                                         }
                                        }}/>
                                    </Grid>
                                    <Grid item>
                                        If the participant is under 13 years of age, a guardian must provide the guardian’s email address and phone number.
                                    </Grid>
                                </Grid>
                            )}

                            {/*STEP 2: Guardian and Emergency Contact Info*/}
                            {activeStep === 1 &&(
                                <Grid container spacing={2} sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    {calculatedAge<18?(
                                        <>    
                                            <SectionDescription title="Guardian Information" description = "Please provide the following information about the primary guardian."/>
                                            <div/>
                                            
                                                

                                            <Grid item>
                                                <LabeledInput label="Guardian Full Name" placeholder="Enter your guardian full name" register={register} name="guardianFullName" error={errors.guardianFullName} rules={{ required:"Full name is required " }} sx={textboxStyle}/>
                                            </Grid>
                                            <Grid item>
                                                {/* <LabeledInput label="Gaurdian Phone Number" placeholder="Enter your phone number" register={register} name="guardianPhoneNumber" rules={{ required: true }} sx={textboxStyle}/> */}
                                                <LabeledPhoneInput label="Guardian Phone Number" control={control} name="guardianPhoneNumber" sx={textboxStyle}  phoneMask={phoneMask} 
                                                    rules={{required:"Phone number is required", validate:(value) =>{
                                                        if (!value || value.length !== 10) {
                                                            return "Please enter all required digits";
                                                        }
                                                        return true;
                                                    }
                                                }}/>
                                                
                                            </Grid>
                                            <Grid item>
                                                {/* <LabeledInput label="Guardian Email" placeholder="Enter your guardian email" register={register} name="guardianEmail" rules={{ required: true }} sx={textboxStyle}/> */}
                                                <LabeledInput label="Guardian Email" placeholder="name@example.com" register={register} name="guardianEmail" rules={{ required: "Invalid email format. Use: name@email.com", pattern:{value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Please enter a valid email address" } }} error= {errors.guardianEmail} sx={textboxStyle} slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <MailOutlinedIcon 
                                                                    sx={{ color:"#686673" }} 
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }
                                        }}
                                        />
                                            </Grid>
                                            <Grid item>
                                                {/* <LabeledInput label="Confirm Guardian Email" placeholder="Confirm your guardian email" register={register} name="confirmGuardianEmail" rules={{ required: true }} sx={textboxStyle}/> */}
                                                <LabeledInput label="Confirm Guardian Email" placeholder="name@example.com" register={register} name="confirmGuardianEmail" rules={{ required: "Invalid. Emails must match ", validate:(value)=> value === watch("guardianEmail") || "Invalid. Emails must match", message: "Invalid. Emails must match."  }} error= {errors.confirmGuardianEmail} sx={textboxStyle} slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <MailOutlinedIcon 
                                                                    sx={{ color:"#686673" }} 
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }
                                        }}
                                        />
                                            </Grid>
                                        </>): 
                                    (
                                        <Grid item xs={12}>
                                            <div style={{
                                                
                                                height: "52px",
                                                width: "942px", 
                                                borderRadius: "16px",
                                                
                                                
                                                backgroundColor: "#2E7D6B", 
                                                border: "1px solid #2E7D6B", 
                                                color: "#ffffffff", 
                                                
                                               
                                                display: "flex",
                                                alignItems: "center",
                                                paddingLeft: "14px", 
                                                paddingRight: "14px",
                                                gap: "12px" 
                                            }}>
                                                
                                                <CheckOutlinedIcon sx={{ color: "#ffffffff" }} />
                                                
                                                
                                                <div style={{ fontSize: "14px", fontWeight: "500" }}>
                                                    Guardian information is not required for participants 18 or older. Please provide a secondary emergency contact in case of emergency.
                                                </div>
                                            </div>
                                        </Grid>
                                    )}

                                    <Divider/>

                                    <SectionDescription title="Secondary Emergency Contact" description = "Please provide the following information about the secondary emergency contact."/>
                                    <Grid item>
                                        <LabeledInput label="Emergency Contact Name" placeholder="Enter emergency contact name" register={register} name="emergencyContactName" error={errors.emergencyContactName} rules={{ required:"Full name is required " }} sx={textboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Relationship" placeholder="Enter your relationship" register={register} name="emergencyContactRelationship" error={errors.emergencyContactRelationship} rules={{ required:"Relationship is required " }} sx={textboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        {/* <LabeledInput label="Emergency Contact Phone" placeholder="Enter your emergency contact phone" register={register} name="emergencyContactPhone" rules={{ required: true }} sx={textboxStyle}/> */}
                                        <LabeledPhoneInput label="Emergency Contact Phone Number" control={control} name="emergencyContactPhoneNumber" sx={textboxStyle}  phoneMask={phoneMask} 
                                                    rules={{required:"Phone number is required", validate:(value) =>{
                                                        if (!value || value.length !== 10) {
                                                            return "Please enter all required digits";
                                                        }
                                                        return true;
                                                    }
                                                }}/>
                                    </Grid>
                                    <Grid item>
                                        {/* <LabeledInput label="Emergency Contact Email" placeholder="Enter your emergency contact email" register={register} name="emergencyContactEmail" rules={{ required: true }} sx={textboxStyle}/> */}
                                        <LabeledInput label="Emergency Contact Email" placeholder="name@example.com" register={register} name="emergencyContactEmail" rules={{ required: "Invalid email format. Use: name@email.com", pattern:{value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Please enter a valid email address" } }} error= {errors.emergencyContactEmail} sx={textboxStyle} slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <MailOutlinedIcon 
                                                                    sx={{ color:"#686673" }} 
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }
                                        }}
                                        />
                                    </Grid>


                                </Grid>
                                
                            )}

                            {/*STEP 3: RDA Member or School Info*/}
                            {activeStep === 2 &&(
                                <Box
                                sx={{ display: "flex", flexDirection: "column", alignItems: "start", width: "100%", alignSelf:"start"}}>
                                    <Grid container justifyContent="center" direction="column" spacing={2} sx={{ width:"100%", paddingLeft:"12px", paddingRight:"12px", display:"flex", justifySelf:"center"}}>
                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'start', textAlign: 'center' }}>
                                            <Typography>Are you a member or alumni of an RDA Member Company?</Typography>
                                        </Grid>
                                        <Grid container spacing={2} sx={{ width:"100%", justifySelf:"start"}}>

                                            <Grid item sx={{ visibility: "hidden", pointerEvents: "none" }}>
                                                    <Box sx={{ ...textboxStyle,}}>
                                                    </Box>
                                                </Grid>

                                                    <Grid item sx={{ visibility: "hidden", pointerEvents: "none" }}>
                                                    <Box sx={{ ...textboxStyle,}}>
                                                    </Box>
                                                </Grid></Grid>

                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'start', textAlign: 'center' }}>
                                            <Controller name="RDAMember" control={control} defaultValue="" rules={{ required: "Please select an option" }} render={({field, fieldState: { error }})=> (
                                                    <FormControl error={!!error}>
                                                        <RadioGroup {...field} row>
                                                            <FormControlLabel value="Yes" control={<Radio sx={radioButtonStyle}/>} label="Yes" />
                                                            <FormControlLabel value="No" control={<Radio sx={radioButtonStyle}/>} label="No" />
                                                        </RadioGroup>
                                                    
                                                 
                                            
                                            {error && (
                                            <FormHelperText sx={{ color: "#FF5555 !important", marginLeft: 0 }}>
                                                {error.message}
                                            </FormHelperText>)}</FormControl>)}/>
                
                                        </Grid>
                                        <Grid item xs={12} sx={{justifySelf:"start", justifyContent:"start", width:"100%"}}>
                                            {RDAMember === 'Yes' && (
                                                <Grid container justifyContent="start" spacing={2} sx={{margin:"0 auto", width:"100%"}}>
                                                    <Grid item>
                                                        <BasicListField label="RDA Region" placeholder="Select Region" control={control} name="RDARegion" rules={{ required: true }} textfieldSx={{...textboxStyle, paddingRight:"16px"}} dropDownSx={dropDownStyle} options={RDARegions}/>
                                                    </Grid>
                                                    <Grid item>
                                                    <BasicListField label="RDA Member Company" placeholder="RDA Member Company" control={control} name="currentRDADanceSchoolCompany" rules={{ required: true }} textfieldSx={{...textboxStyle, paddingRight:"16px"}} dropDownSx={dropDownStyle} options={RDAMemberCompanies}/>
                                                    </Grid>
                                                    {/* <LabeledInput label="RDA Member Company Name" placeholder="RDA Member Company Name" register={register} name="currentRDADanceSchoolCompany" error={errors.currentRDADanceSchoolCompany} rules={{ required:"School name is required " }} sx={{...textboxStyle}}/> */}
                                                </Grid>
                                            )}  
                                            {RDAMember === 'No' && (
                                                <Grid container spacing={2} sx={{ width:"100%", justifySelf:"start"}}>
                                                    <LabeledInput label="Current Dance School or Company" placeholder="Current Dance School or Company" register={register} name="currentDanceSchoolCompany" error={errors.currentDanceSchoolCompany} rules={{ required:"School name is required " }} sx={{...textboxStyle,}}/>

                                                    <Grid item sx={{ visibility: "hidden", pointerEvents: "none" }}>
                                                    <Box sx={{ ...textboxStyle,}}>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                    
                                </Box>
                            )}

                            {/*STEP 4: Program Selection*/}
                            {activeStep === 3 &&(<Box>
                                <Grid item xs={12} sx={{maxWidth:'1012px', width:"100%", marginBottom:"36px", paddingLeft:"12px"}}>
                                        <SectionDescription title="Program Selection" description = "Select one or more programs you'd like to register for:"/>
                                    </Grid>
                                <Grid container spacing={2} sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    
                                    {events.map((event) => (
                                        <Grid item xs={12} md={6} key={event.id}>
                                            <EventCard event={event} control={control} fieldName={event.id} />
                                        </Grid>
                                    ))}
                                
                                </Grid>
                                {selectionError && (
                                    <Typography sx={{ color: 'error.main', mt: 2, textAlign: 'center', fontWeight: 'bold' }}>
                                        Please select at least one program to continue.
                                    </Typography>
                                )}
                                </Box>
                            )}

                            {/*STEP 5: Health and Safety */}
                            {activeStep === 4 &&(
                                <Grid container spacing={2} alignItems="flex-start" sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    
                                    <SectionDescription title="Health & Safety Information" description = "Please provide the following health and safety information to help Regional Dance America ensure appropriate care during the program. If there is not relevant information, please type N/A into the field."/>
                                    <Grid item>
                                        <LabeledInput multiline rows={3} label="Allergies (Food, Drug, Environmental)" placeholder="List any known allergies..." register={register} name="allergies" error={errors.allergies} rules={{ required: "Enter relevant information or N/A" }} sx={multilineTextboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput multiline rows={3} label="Current Prescribed Medications" placeholder="The pill bottle must match written instructions, if prescribed by a physician. Include ALL of the following information: Drug Name, Dosage, How Often / Times to Administer." register={register} name="prescribedMedications" error={errors.prescribedMedications} rules={{ required: "Enter relevant information or N/A" }} sx={multilineTextboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput multiline rows={6} label="Current Prescribed Medications that are classified as controlled substances" placeholder="If any… 
                                            Regional Dance America will contact the Participant (if 18 or older) or the Primary Guardian to obtain written permission to: store this medication in its original container for Participant to have at any necessary times, and Participant will be able to get the medication from RDA when needed to self-administer." 
                                            register={register} name="prescribedNarcotics" error={errors.prescribedNarcotics} rules={{ required: "Enter relevant information or N/A" }} sx={multilineTextboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput multiline rows={6} label="Over-the-Counter Medications" placeholder="List any OTC medications regularly taken..." register={register} name="OTCMedication" error={errors.OTCMedication} rules={{ required: "Enter relevant information or N/A" }} sx={multilineTextboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput multiline rows={6} label="Medical Conditions" placeholder="e.g., asthma, diabetes, injuries..." register={register} name="medicalConditions" error={errors.medicalConditions} rules={{ required: "Enter relevant information or N/A" }}  sx={multilineTextboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <FormControl error={!!errors.emergency} component="fieldset">
                                            <FormLabel component="legend" sx={{ color: '#F8F5FE', mb: 1, fontSize:"14px", marginBottom:"8px",marginTop:"10px",'&.Mui-focused': {color: '#F8F5FE', } }}>
                                            Check the appropriate box(es) below if the Participant has any of the following
                                            </FormLabel>
                                            <FormGroup sx={{background:"#1C1A25", borderColor:"#302C3F", borderWidth:"1px", paddingLeft:"8px", borderRadius:"16px"}}>
                                            {medicalConditions.map((option) => (
                                                <Controller
                                                key={option}
                                                name="emergency" 
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                    label={option}
                                                    control={
                                                        <Checkbox
                                                        sx={radioButtonStyle}
                                                        checked={field.value?.includes(option)}
                                                        onChange={() => {
                                                            const nextValue = field.value?.includes(option)
                                                            ? field.value.filter((v) => v !== option)
                                                            : [...(field.value || []), option];
                                                            field.onChange(nextValue);
                                                        }}
                                                        />
                                                    }
                                                    />
                                                )}
                                                />
                                            ))}
                                            </FormGroup>

                                            {errors.emergency && (
                                            <FormHelperText sx={{ color: "#FF5555 !important", marginLeft: 0 }}>
                                                {errors.emergency.message}
                                            </FormHelperText>
                                            )}
                                        </FormControl>
                                        <Typography sx={{width:"490px", fontSize:"14px"}}>
                                            If any boxes are checked: Regional Dance America will contact the Participant (if 18 or older) or the Primary Guardian to obtain an Emergency Action Plan (EAP). An EAP from the Participant’s physician along with rescue medications will be required. If Epi-pen is needed, two (2) pens are required.
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Physician’s Name" placeholder="Enter physician’s name" register={register} name="physicianName" error={errors.physicianName} rules={{ required: "Enter relevant information or N/A" }} sx={textboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        {/* <LabeledInput label="Physician’s Phone Number" placeholder="Enter phone number" register={register} name="physicianPhoneNumber" rules={{ required: true }} sx={textboxStyle}/> */}
                                        <LabeledPhoneInput label="Physician's Phone Number" control={control} name="physicianPhoneNumber" sx={textboxStyle}  phoneMask={phoneMask} 
                                            rules={{required:"Enter relevant information or N/A", validate:(value) =>{
                                                if (!value || value.length !== 10) {
                                                    return "Please enter all required digits";
                                                }
                                                return true;
                                            }
                                        }}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Health Insurance Provider" placeholder="Enter health insurance provider" register={register} name="healthInsuranceProvider" error={errors.healthInsuranceProvider} rules={{ required: "Enter relevant information or N/A" }} sx={textboxStyle}/>
                                    </Grid>
                                    <Grid item>
                                        <LabeledInput label="Policy Number" placeholder="Enter policy number" register={register} name="policyNumber" error={errors.policyNumber} rules={{ required: "Enter relevant information or N/A" }} sx={textboxStyle}/>
                                    </Grid>
                                    <Box sx={{display:"flex",flexDirection:"column", width:"100%"}}>
                                        <SectionDescription title="Medical Card" description = "Upload images of your medical card (front and back)"/>
                                        <Button
                                            component="label"
                                            role={undefined}
                                            variant="contained"
                                            tabIndex={-1}
                                            startIcon={<CloudUploadIcon />}
                                            sx={{
                                                marginTop:"12px",
                                                color: "#CDCCD0",
                                                border: "2px dashed #CDCCD0",
                                                borderRadius:999,
                                                backgroundColor:"rgba(255, 255, 255, 0)",
                                                textTransform:"none",
                                                fontSize:14,
                                                width:'100%',
                                                height:52, }}
                                            >
                                            Upload files
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    multiple

                                                />
                                        </Button>
                                        <input 
                                            type="hidden" 
                                            {...register("fileUploads", {
                                                validate: (val) => (val?.length > 0 && val?.length <= 2) || "Please have 1 or 2 files to show front and back"
                                            })} 
                                        />
                                    </Box>
                                    <Box/>
                                    <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
        
                                        {currentFiles && currentFiles.map((file, index) => (
                                            <Box 
                                                key={`${file.name}-${index}`}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    backgroundColor: "#1C1A25", 
                                                    border: "1px solid #302C3F",
                                                    borderRadius: "8px",
                                                    padding: "8px 12px",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" }}>
                                                    <InsertDriveFileOutlinedIcon sx={{ color: "#686673" }} />
                                                    <Typography 
                                                        noWrap 
                                                        sx={{ color: "#F8F5FE", fontSize: "14px", maxWidth: "300px" }}
                                                    >
                                                        {file.name}
                                                    </Typography>
                                                </Box>

                                                <IconButton 
                                                    onClick={() => handleRemoveFile(index)}
                                                    size="small"
                                                    sx={{ color: "#FF5555" }} 
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                    {errors.fileUploads && (
                                        <Typography sx={{ color: "#FF5555", fontSize: "12px", mt: 1, fontWeight: "bold", textAlign: 'center' }}>
                                            {errors.fileUploads.message}
                                        </Typography>
                                    )}
                                    <Grid item>
                                        <SectionDescription title="Emergency Medical Treatment Consent" description = {`${dancerName} (Summer Dance Intensive Participant) has permission to participate in Regional Dance America’s 2026 summer intensive program(s). By signing below, I (OR if the Participant is under 18 years of age, this Form must be signed by a Parent or Legal Guardian) hereby give permission to Regional Dance America (“RDA”) to give consent for treatment.`}/>
                                        {calculatedAge>=18 &&(
                                            <>
                                            <Typography sx={{width:"490px", fontSize:"14px",marginTop:"8px"}}>
                                                Participant Signature
                                            </Typography>
                                            <Controller
                                                name="dancerHealthSignature"
                                                control={control}
                                                defaultValue=""
                                                rules={{ required: "A signature is required" }}
                                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <Box>
                                                    <ReactSketchCanvas
                                                        key={canvasKey}
                                                        ref={dancerHealthSign}
                                                        allowOnlyPointerType="all"
                                                        canvasColor="#f8f5fe25"
                                                            // strokeColor={strokeColor}
                                                        strokeColor="black"
                                                        style={{ border: error? '1px solid red' : '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                                        strokeWidth={4}
                                                        onStroke={() => {
                                                            dancerHealthSign.current.exportImage("png").then((data) => onChange(data));
                                                        }}
                                                    />
                                                    {error && (
                                                        <Typography color="error" variant="caption">
                                                        {error.message}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}/>
                                            </>
                                        )}
                                        {calculatedAge<18 &&(
                                            <>
                                            <LabeledInput label="Guardian Full Name" placeholder="Enter guardian's full name" register={register} name="guardianFullNameHealthSign" error={errors.guardianFullNameHealthSign} rules={{ required:"Full name is required" }} sx={textboxStyle}/>
                                            <Typography sx={{width:"490px", fontSize:"14px"}}>
                                            Guardian Signature
                                            </Typography>   
                                            <Controller
                                            name="guardianHealthSignature"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "A signature is required" }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <Box>
                                                <ReactSketchCanvas
                                                    key={canvasKey}
                                                    ref={guardianHealthSign}
                                                    allowOnlyPointerType="all"
                                                    canvasColor="#f8f5fe25"
                                                        // strokeColor={strokeColor}
                                                    strokeColor="black"
                                                    style={{ border: error? '1px solid red' : '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                                    strokeWidth={4}
                                                    onStroke={() => {
                                                        guardianHealthSign.current.exportImage("png").then((data) => onChange(data));
                                                    }}
                                                />
                                                {error && (
                                                    <Typography color="error" variant="caption">
                                                    {error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}/>
                                        </>
                                        )

                                        }

                                    </Grid>
                                    


                                </Grid>
                                
                            )}

                            {/*STEP 6: Housing and Meals*/}
                            {activeStep === 5 &&(
                                <Box>
                                {DCICarlisle26watch && DCICarlisle26watch.trim() !== "" ? (
                                    <Box>
                                    <Grid container spacing={2} wrap="nowrap"  sx={{ display: "flex",maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px", flexDirection:"column"}}>
                                        <Grid item >
                                            <Typography>On Campus Housing & Meals- $650 (OPTIONAL)</Typography>
                                            <Typography>Live in supervised on-campus dormitory housing during the program with meals (breakfast,  lunch, dinner). Staying on campus is completely optional, but if the participant stays in the  on-campus dormitory, the meal plan is required. Total for dormitory + meals is $650. This option  is only available for the Dance & Choreography Intensive program in Carlisle, PA.</Typography>
                                        </Grid>
                                        {/* <Grid item sx={{display:'flex',justifyContent:"center", alignItems:"center"}}>
                                            <Switch sx={{...switchStyle, paddingBottom:"12px"}}{...register("housingCheck")}/>
                                        </Grid> */}
                                        <Grid item sx={{ display: 'flex', justifyContent: "start", alignItems: "center" }}>
                                            <Controller
                                                name="housingCheck"
                                                control={control}
                                                defaultValue="No" 
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <RadioGroup {...field} row>
                                                            <FormControlLabel 
                                                                value="Yes" 
                                                                control={<Radio sx={radioButtonStyle} />} 
                                                                label="Yes" 
                                                            />
                                                            <FormControlLabel 
                                                                value="No" 
                                                                control={<Radio sx={radioButtonStyle} />} 
                                                                label="No" 
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                )}
                                            />
                                        </Grid>                                      
                                    </Grid>
                                    <Collapse in={watch("housingCheck") === "Yes"}>
                                        <Box sx={{paddingLeft:"12px"}}>
                                            <LabeledInput label="Roommate Request (Optional)" placeholder="Enter name of preferred roommate" register={register} name="preferredRoommate" rules={{ required: true }} sx={{...textboxStyle, width:'100%', }}/>
                                            <LabeledInput multiline rows={4} label="Dietary Restrictions for on-campus meals" placeholder="e.g., vegetarian, vegan, gluten-free, allergies, religious requirements..." register={register} name="dietaryRestrictions" error={errors.dietaryRestrictions} rules={{ required: "Enter relevant information or N/A" }} sx={{...multilineTextboxStyle, width:'100%', height:"auto" }}/>
                                        </Box>
                                    </Collapse>

                                </Box>
                                    ) : (
                                    <Box 
                                        sx={{ 
                                        p: 3, 
                                        border: '1px solid #302C3F', 
                                        borderRadius: '16px', 
                                        backgroundColor: '#1C1A25',
                                        textAlign: 'center' 
                                        }}
                                    >
                                        <Typography sx={{ color: '#686673', fontSize: '14px' }}>
                                        Chaperoned housing and meals are not available for the program(s) your Participant is attending. Please click Next to continue.
                                        </Typography>
                                    </Box>
                                    )}
                                
                                </Box>
                            )}
                            
                            {/*STEP 7: Merchandise*/}
                            {activeStep === 6 &&(
                                <Grid direction="column" container spacing={2} sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    <SectionDescription title="Merchandise Pre-Order" description = "Pre-order RDA merchandise (optional). Please click Next to continue."/>
                                    <div></div>
                                    {DCIRosewell26watch && DCIRosewell26watch.trim() !== "" && (
                                        <>
                                            <Divider sx={{width:"100%", bgcolor:"#F8F5FE"}}/>
                                            <Typography sx={{width:"100%", fontSize:"20px",}}>
                                                Roswell, GA - Dance & Choreography Intensive Merchandise
                                            </Typography>
                                            {DCImerch.map((item) => (
                                                <Controller
                                                    key={item.id}
                                                    // This name links the quantity to this specific piece of merch
                                                    name={`Rosewell${item.id}Quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <MerchInput
                                                            merchName={item}
                                                            sizes={sizes}
                                                            register={register}
                                                            control={control}
                                                            namePrefix="Rosewell"
                                                            // Passing 'field' allows the arrows to work
                                                            field={field} 
                                                            sx={dropDownStyle}
                                                            dropDownSx={dropDownStyle}
                                                            textfieldSx={{ ...textboxStyle, paddingRight: "16px" }}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </>
                                    )}
                                    {DCILawrence26watch && DCILawrence26watch.trim() !== "" && (
                                        <>
                                        <Divider sx={{bgcolor:"#F8F5FE"}}/>
                                            <Typography sx={{width:"100%", fontSize:"20px"}}>
                                                Lawrence, KS - Dance & Choreography Intensive Merchandise
                                            </Typography>
                                            {DCImerch.map((item) => (
                                                <Controller
                                                    key={item.id}
                                                    // This name links the quantity to this specific piece of merch
                                                    name={`Lawrence${item.id}Quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <MerchInput
                                                            merchName={item}
                                                            sizes={sizes}
                                                            register={register}
                                                            control={control}
                                                            namePrefix="Lawrence"
                                                            // Passing 'field' allows the arrows to work
                                                            field={field} 
                                                            sx={dropDownStyle}
                                                            dropDownSx={dropDownStyle}
                                                            textfieldSx={{ ...textboxStyle, paddingRight: "16px" }}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </>
                                    )}
                                    {DCICarlisle26watch && DCICarlisle26watch.trim() !== "" && (
                                        <>
                                        <Divider sx={{bgcolor:"#F8F5FE"}}/>
                                            <Typography sx={{width:"100%", fontSize:"20px"}}>
                                                Carlisle, PA - Dance & Choreography Intensive Merchandise
                                            </Typography>
                                            {DCImerch.map((item) => (
                                                <Controller
                                                    key={item.id}
                                                    // This name links the quantity to this specific piece of merch
                                                    name={`Carlisle${item.id}Quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <MerchInput
                                                            merchName={item}
                                                            sizes={sizes}
                                                            register={register}
                                                            control={control}
                                                            namePrefix="Carlisle"
                                                            // Passing 'field' allows the arrows to work
                                                            field={field} 
                                                            sx={dropDownStyle}
                                                            dropDownSx={dropDownStyle}
                                                            textfieldSx={{ ...textboxStyle, paddingRight: "16px" }}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </>
                                    )}
                                    {PWKingwood26watch && PWKingwood26watch.trim() !== "" && (
                                        <>
                                        <Divider sx={{bgcolor:"#F8F5FE"}}/>
                                            <Typography sx={{width:"100%", fontSize:"20px"}}>
                                                Kingwood, TX - Performance Workshop
                                            </Typography>
                                            {PWmerch.map((item) => (
                                                <Controller
                                                    key={item.id}
                                                    // This name links the quantity to this specific piece of merch
                                                    name={`Kingwood${item.id}Quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <MerchInput
                                                            merchName={item}
                                                            sizes={sizes}
                                                            register={register}
                                                            control={control}
                                                            namePrefix="Kingwood"
                                                            // Passing 'field' allows the arrows to work
                                                            field={field} 
                                                            sx={dropDownStyle}
                                                            dropDownSx={dropDownStyle}
                                                            textfieldSx={{ ...textboxStyle, paddingRight: "16px" }}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </>
                                    )}
                                    
                                </Grid>
                                
                            )}

                            {/*STEP 8: Transportation*/}
                            {activeStep === 7 &&(
                                <Grid container spacing={2} sx={{display:'flex', flexDirection:'column',maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    <SectionDescription title="Transportation" description = "Adults authorized for participant pick-up/drop-off:"/>
                                    {fields.map((item, i) => (
                                        <AuthorizedTransport 
                                        key={item.id} 
                                        index={i} 
                                        register={register} 
                                        sx={textboxStyle}
                                        control={control}
                                        errors={errors}
                                        onRemove={() => remove(i)}
                                        showRemove={fields.length > 1}
                                        />
                                    ))}
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<AddOutlinedIcon/>}
                                        onClick={() => append({ name: "", relationship: "", phone: "", email: "" })}
                                        sx={{
                                            color: "#CDCCD0",
                                            border: "2px dashed #CDCCD0",
                                            borderRadius:999,
                                            backgroundColor:"rgba(255, 255, 255, 0)",
                                            textTransform:"none",
                                            fontSize:14,
                                            width:'100%',
                                            height:52, }}
                                    >
                                        Add Another Authorized Person
                                    </Button>
                                    
                                </Grid>
                                
                            )}

                            {/*STEP 9: DCI Observers and Tickets*/}
                            {activeStep === 8 &&(
                                <Grid container spacing={2} sx={{maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    {/* const anyEventTickets = DCIRosewell26watch.trim() !== ""||DCILawrence26watch.trim() !== ""||DCICarlisle26watch.trim() !== ""; */}
                                    {!anyEventTickets ? (
                                        <Box 
                                        sx={{ 
                                            p: 3, 
                                            border: '1px solid #302C3F', 
                                            borderRadius: '16px', 
                                            backgroundColor: '#1C1A25',
                                            textAlign: 'center',
                                            minWidth:"450px"
                                        }}
                                    >
                                        <Typography sx={{ color: '#686673', fontSize: '14px' }}>
                                        Observer passes and performance tickets are not available for the program(s) your Participant is attending. Please click Next to continue.
                                        </Typography>
                                    </Box>
                                    ):(
                                        <SectionDescription title="DCI Observers & Tickets" description = "On the last day of each DCI program, the dancers and choreographers will present a  choreography showcase performance at 11:30 am. It is open to family, friends, teachers, and  the public, and tickets are $10 per guest. Please include the number of guest tickets your party requires (optional). You may order a guest observation pass(es) for any day of the DCI. Full-day observer passes are $50 each; nightly showcase performance-only passes are $10 each. To conclude each DCI program, the dancers and choreographers will present a final choreography showcase performance at 11:30 am on the final Saturday. It is open to family, friends, teachers, and the public. Final showcase performance tickets are $10 per guest. Please include the number of guest tickets your party requires (optional). Please click Next to continue"/>
                                    )

                                    }
                                    {DCIRosewell26watch?.trim() !== ""&& 
                                    <>
                                        <div>Observer Passes for Roswell Event</div>
                                            <Box sx={{display:"flex", gap:2, flexWrap: "wrap", justifyContent:"flex-start"}}>
                                                <div>
                                                    <LabeledInput label="Full Day Observer Passes ($50 each)" placeholder="0" register={register} name="fullDayPassesRosewell26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of full-day passes requested</div>
                                                </div>
                                                <div>
                                                    <LabeledInput label="Choreography Showing Only ($10 each)" placeholder="0" register={register} name="choreographyPassesRosewell26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of choreography-only passes requested</div>
                                                </div>
                                            </Box>
                                            
                                            <Box sx={{width:"100%"}}>
                                                <div>Final Showcase Performance Tickets - Roswell, GA ($10 each)</div>
                                                <div>
                                                    <LabeledInput label="Guest Tickets" placeholder="0" register={register} name="guestTicketsRosewell26" rules={{ required: true }} sx={{...textboxStyle, width:"100%"}}/>
                                                    <div className={styles.labelText}> Number of tickets for the final showcase performance</div>
                                                </div>
                                            </Box>
                                    </>
                                    }
                                    {DCILawrence26watch?.trim() !== ""&& 
                                    <>
                                        <div>Observer Passes for Lawrence Event</div>
                                            <Box sx={{display:"flex", gap:2, flexWrap: "wrap", justifyContent:"flex-start"}}>
                                                <div>
                                                    <LabeledInput label="Full Day Observer Passes ($50 each)" placeholder="0" register={register} name="fullDayPassesLawrence26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of full-day passes requested</div>
                                                </div>
                                                <div>
                                                    <LabeledInput label="Choreography Showing Only ($10 each)" placeholder="0" register={register} name="choreographyPassesLawrence26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of choreography-only passes requested</div>
                                                </div>
                                            </Box>
                                            
                                            <Box sx={{width:"100%"}}>
                                                <div>Final Showcase Performance Tickets - Lawrence, KS ($10 each)</div>
                                                <div>
                                                    <LabeledInput label="Guest Tickets" placeholder="0" register={register} name="guestTicketsLawrence26" rules={{ required: true }} sx={{...textboxStyle, width:"100%"}}/>
                                                    <div className={styles.labelText}> Number of tickets for the final showcase performance</div>
                                                </div>
                                            </Box>
                                    </>
                                    }
                                    {DCICarlisle26watch?.trim() !== ""&& 
                                    <>
                                        <div>Observer Passes for Carlisle Event</div>
                                            <Box sx={{display:"flex", gap:2, flexWrap: "wrap", justifyContent:"flex-start"}}>
                                                <div>
                                                    <LabeledInput label="Full Day Observer Passes ($50 each)" placeholder="0" register={register} name="fullDayPassesCarlisle26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of full-day passes requested</div>
                                                </div>
                                                <div>
                                                    <LabeledInput label="Choreography Showing Only ($10 each)" placeholder="0" register={register} name="choreographyPassesCarlisle26" rules={{ required: true }} sx={{...textboxStyle}}/>
                                                    <div className={styles.labelText}> Number of choreography-only passes requested</div>
                                                </div>
                                            </Box>
                                            
                                            <Box sx={{width:"100%"}}>
                                                <div>Final Showcase Performance Tickets - Carlisle, PA ($10 each)</div>
                                                <div>
                                                    <LabeledInput label="Guest Tickets" placeholder="0" register={register} name="guestTicketsCarlisle26" rules={{ required: true }} sx={{...textboxStyle, width:"100%"}}/>
                                                    <div className={styles.labelText}> Number of tickets for the final showcase performance</div>
                                                </div>
                                            </Box>
                                    </>
                                    }
                                </Grid>
                                
                            )}

                            {/*STEP 10: Acknowledgement and Signature*/}
                            {activeStep === 9 &&(
                                <Grid container spacing={2} sx={{display:"flex", flexDirection:"column", maxWidth:'1012px', width:"100%", paddingLeft:"12px", paddingRight:"12px"}}>
                                    <SectionDescription title="Acknowledgement & Signature" description = "Please review the agreement below and sign to confirm that all provided information is accurate. Your signature is required to complete your application. Please click See Agreement, below, to review the PARTICIPANT RELEASE AND WAIVER OF LIABILITY. The Agreement will open in a separate tab for easy viewing. After reviewing the Agreement, click on the boxes below to provide the Participant’s signature and requested information, and the Guardian’s signature and requested information, if the Participant is under 18 years of age. Once you have completed all fields, click Download Signed Agreement to download the completed Agreement."/>
                                    {DCIRosewell26watch && DCIRosewell26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Roswell Dance Program Participant Release Form</div>
                                        <div>
                                        <Button variant="contained" sx={navButtonStyle} onClick={()=>handleSeeAgreement("2026RosewellWaiver.pdf")}>See Agreement</Button>
                                        {/* <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px"}} onClick={()=>handleDownloadAgreement("2026RosewellWaiver.pdf")}>Download Agreement</Button> */}
                                        </div>
                                    </Box>
                                    )}
                                    {DCILawrence26watch && DCILawrence26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Lawrence Dance Program Participant Release Form</div>
                                        <div>
                                        <Button variant="contained" sx={navButtonStyle} onClick={()=>handleSeeAgreement("2026LawrenceWaiver.pdf")}>See Agreement</Button>
                                        {/* <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px"}} onClick={()=>handleDownloadAgreement("2026LawrenceWaiver.pdf")}>Download Agreement</Button> */}
                                        </div>
                                    </Box>
                                    )}
                                    {DCICarlisle26watch && DCICarlisle26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Carlisle Dance Program Participant Release Form</div>
                                        <div>
                                        <Button variant="contained" sx={navButtonStyle} onClick={()=>handleSeeAgreement("2026CarlisleWaiver.pdf")}>See Agreement</Button>
                                        {/* <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px"}} onClick={()=>handleDownloadAgreement("2026CarlisleWaiver.pdf")}>Download Agreement</Button> */}
                                        </div>
                                    </Box>
                                    )}
                                    {PWKingwood26watch && PWKingwood26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Kingwood Dance Program Participant Release Form</div>
                                        <div>
                                        <Button variant="contained" sx={navButtonStyle} onClick={()=>handleSeeAgreement("2026KingwoodWaiver.pdf")}>See Agreement</Button>
                                        {/* <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px"}} onClick={()=>handleDownloadAgreement("2026KingwoodWaiver.pdf")}>Download Agreement</Button> */}
                                        </div>
                                    </Box>
                                    )}
                                    {/* <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Summer Dance Programs Participant Release Form</div>
                                        <Button variant="contained" sx={navButtonStyle} onClick={handleSeeAgreement}>See Agreement</Button>
                                    </Box> */}
                                    <SectionDescription title="Participant Signature" description = ""/>
                                    {/* <Box >
                                        <ReactSketchCanvas
                                            ref={canvasRef}
                                            canvasColor="#f8f5fe25"
                                                // strokeColor={strokeColor}
                                            strokeColor="black"
                                            style={{ border: '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                            strokeWidth={4}
                                            
                                        />

                                        <Button onClick={clearCanvas} size="small" sx={{ mt: 1 }}>
                                            Clear Signature
                                        </Button>
                                    </Box> */}
                                    <Controller
                                            name="participantWaiverSignature"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "A signature is required" }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <Box>
                                                <ReactSketchCanvas
                                                    key={canvasKey}
                                                    ref={canvasRef}
                                                    allowOnlyPointerType="all"
                                                    canvasColor="#f8f5fe25"
                                                        // strokeColor={strokeColor}
                                                    strokeColor="black"
                                                    style={{ border: error? '1px solid red' : '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                                    strokeWidth={4}
                                                    onStroke={() => {
                                                        canvasRef.current.exportImage("png").then((data) => onChange(data));
                                                    }}
                                                />
                                                {error && (
                                                    <Typography color="error" variant="caption">
                                                    {error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}/>
                                    <LabeledInput label="Participant Full Legal Name" placeholder="Participant Full Name" register={register} name="printedSignName" error={errors.printedSignName} rules={{ required: "Enter Participant Full Name" }} sx={{...textboxStyle, width:'100%', }}/>
                                    <LabeledInput label="Street Address" placeholder="Type Participant's Street Address" register={register} name="participantStreetAddress" error={errors.participantStreetAddress} rules={{ required: "Enter Participant Street Address" }} sx={{...textboxStyle, width:'100%', }}/>
                                    <LabeledInput label="City/State/Zip" placeholder="Type Participant City/State/Zipcode" register={register} name="participantCityStateZipcode" error={errors.participantCityStateZipcode} rules={{ required: "Enter Participant City State and Zipcode" }} sx={{...textboxStyle, width:'100%', }}/>
                                    
                                    
                                    {calculatedAge<18&&(
                                        <>    
                                            <SectionDescription title="Guardian Signature" description = ""/>
                                            <Box >
                                        
                                            {/* <ReactSketchCanvas
                                                ref={guardianFinalSign}
                                                canvasColor="#f8f5fe25"
                                                // strokeColor={strokeColor}
                                                strokeColor="black"
                                                style={{ border: '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                                strokeWidth={4}
                                                
                                            />

                                            <Button onClick={clearCanvas} size="small" sx={{ mt: 1 }}>
                                                Clear Signature
                                            </Button> */}

                                                <Controller
                                            name="GuardianWaiverSignature"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "A signature is required" }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <Box>
                                                <ReactSketchCanvas
                                                    key={canvasKey}
                                                    ref={guardianFinalSign}
                                                    allowOnlyPointerType="all"
                                                    canvasColor="#f8f5fe25"
                                                        // strokeColor={strokeColor}
                                                    strokeColor="black"
                                                    style={{ border: error? '1px solid red' : '1px dashed #F8F5FE', width: '100%', height: '200px', background:"#fdfbfb04", display:"flex", justifyContent:"center", touchAction:"none", pointerEvents:"auto", zIndex:10, transform: 'translateZ(0)', }}
                                                    strokeWidth={4}
                                                    onStroke={() => {
                                                        guardianFinalSign.current.exportImage("png").then((data) => onChange(data));
                                                    }}
                                                />
                                                {error && (
                                                    <Typography color="error" variant="caption">
                                                    {error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}/>

                                        </Box>
                                        <LabeledInput label="Guardian Full Legal Name" placeholder="Guardian Full Name" register={register} name="parentSignName" error={errors.parentSignName} rules={{ required: "Enter Parent/Guardian Full Name" }} sx={{...textboxStyle, width:'100%', }}/>
                                        <LabeledInput label="Street Address" placeholder="Type Guardian's Street Address" register={register} name="parentStreetAddress" error={errors.parentStreetAddress} rules={{ required: "Enter Parent/Guardian Street Address" }} sx={{...textboxStyle, width:'100%', }}/>
                                        <LabeledInput label="City/State/Zipcode" placeholder="Type Guardian's City/State/Zipcode" register={register} name="parentCityStateZipcode" error={errors.parentCityStateZipcode} rules={{ required: "Enter Parent/Guardian City State and Zipcode" }} sx={{...textboxStyle, width:'100%', }}/>
    
                                        </>)}
                                        <Box>Click to download the signed agreement, then click submit to submit the registration form. </Box>
                                    {DCIRosewell26watch && DCIRosewell26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Roswell Dance Program Participant Release Form Download</div>
                                        <div>
                                        <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px", backgroundColor:"#22920bff"}} onClick={()=>handleDownloadAgreement("2026RosewellWaiver.pdf")}>Download Signed Agreement</Button>
                                        </div>
                                    </Box>
                                    )}
                                    {DCILawrence26watch && DCILawrence26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Lawrence Dance Program Participant Release Form Download</div>
                                        <div>
                                        <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px", backgroundColor:"#22920bff"}} onClick={()=>handleDownloadAgreement("2026LawrenceWaiver.pdf")}>Download Signed Agreement</Button>                                        </div>
                                    </Box>
                                    )}
                                    {DCICarlisle26watch && DCICarlisle26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Carlisle Dance Program Participant Release Form Download</div>
                                        <div>
                                        <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px", backgroundColor:"#22920bff"}} onClick={()=>handleDownloadAgreement("2026CarlisleWaiver.pdf")}>Download Signed Agreement</Button>
                                        </div>
                                    </Box>
                                    )}
                                    {PWKingwood26watch && PWKingwood26watch.trim() !== "" && (
                                        <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                                        <div>2026 RDA Kingwood Dance Program Participant Release Form Download</div>
                                        <div>
                                        <Button variant="contained" sx={{...navButtonStyle, marginLeft:"5px", backgroundColor:"#22920bff"}} onClick={()=>handleDownloadAgreement("2026KingwoodWaiver.pdf")}>Download Signed Agreement</Button>
                                        </div>
                                    </Box>
                                    )}
                                    
                                    
                                </Grid>
                                
                            )}



                        </form>
                        </Box>
                    </div>
                    <div className={styles.botRight}>
                            <Box sx={{width:"100%",display:"flex", justifyContent:"space-around", paddingTop:"12px", borderStyle:"solid", borderColor:"#232226",borderWidth:"1px 0 0 0", paddingLeft:"20px"}}>
                                <Button variant="contained" sx={navButtonStyle} startIcon={<ArrowBackIcon/>} onClick={handleBack}> Back</Button>
                                {activeStep === 9 ? 
                                    <Button key="submitButton" type="submit" form="PersonalInfo" variant="contained" sx={{...exitButtonStyle, width:"200px"}} endIcon={<CheckIcon/>}> Submit Profile</Button> : 
                                    <Button key="nextButton" variant="contained" sx={{...exitButtonStyle, width:"200px"}} endIcon={<ArrowForwardIcon/>} onClick={handleNext} type="button"> Next</Button>
                                }
                            </Box>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SubmisssionForm;